import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import translationEn from '@/translations/en/hello.json';
import translationPl from '@/translations/pl/hello.json';
import commonEn from '@/translations/en/common.json';
import commonPl from '@/translations/pl/common.json';
import commonEs from '@/translations/es/common.json';
import translationNl from '@/translations/nl/hello.json';
import commonNl from '@/translations/nl/common.json';
import translationFr from '@/translations/fr/hello.json';
import commonFr from '@/translations/fr/common.json';
import translationDe from '@/translations/de/hello.json';
import commonDe from '@/translations/de/common.json';

export const LANGUAGES = {
  EN: 'en',
  PL: 'pl',
  ES: 'es',
  NL: 'nl',
  FR: 'fr',
  DE: 'de',
  IT: 'it',
} as const;

export type Language = (typeof LANGUAGES)[keyof typeof LANGUAGES];

export const LANGUAGE_LABELS: Record<Language, string> = {
  en: 'English',
  pl: 'Polski',
  es: 'Español',
  nl: 'Nederlands',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
};

type JsonObject = Record<string, unknown>;

function deepMerge(base: JsonObject, overrides: JsonObject): JsonObject {
  const out: JsonObject = { ...base };
  for (const [key, value] of Object.entries(overrides)) {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      out[key] &&
      typeof out[key] === 'object' &&
      !Array.isArray(out[key])
    ) {
      out[key] = deepMerge(out[key] as JsonObject, value as JsonObject);
    } else {
      out[key] = value;
    }
  }
  return out;
}

const commonEnOverrides: JsonObject = {
  social: {
    you: 'You',
    backToFeed: 'Back to Feed',
    pets: 'Pets',
    hashtags: 'Hashtags',
    postOptions: 'Post options',
    edit: 'Edit',
    delete: 'Delete',
    edited: 'Edited',
    unlike: 'Unlike',
    savedPosts: 'Your saved posts',
    editPost: 'Edit Post',
    photo: 'Photo',
    caption: 'Caption',
    tagPets: 'Tag pets',
    saveChanges: 'Save changes',
    storyRemoved: 'Story removed.',
    deleteStory: 'Delete your story',
    previousStory: 'Previous story',
    nextStory: 'Next story',
    replyToStory: 'Reply to story...',
    preview: 'Preview',
    cropAndEdit: 'Crop & Edit',
    previewN: 'Preview {{n}}',
    filesSelectedSingle: 'file selected',
    filesSelectedPlural: 'files selected',
    yourPost: 'Your post',
    photoN: 'Photo {{n}}',
    thisIsWhatYouAreSharing: "This is what you're sharing",
    loadingPets: 'Loading pets...',
    noPetsMatchSearch: 'No pets match your search.',
    addOrFollowPetsToTag:
      'Add a pet to your profile or follow others to tag their pets.',
    searchByPetName: 'Search by pet name...',
    addLocationOptional: 'Add Location (Optional)',
    whoCanSeeThis: 'Who can see this?',
    anyone: 'Anyone',
    onlyFollowers: 'Only followers',
    unfollowedName: 'Unfollowed {{name}}',
    followingName: 'Following {{name}}!',
    challengeSelected: 'Challenge {{tag}} selected',
    searchResultsFor: 'Search results for "{{query}}"',
    postsCountSingle: '{{count}} post',
    postsCountPlural: '{{count}} posts',
    noSearchResultsFor: 'No pets, hashtags, or posts found for "{{query}}".',
    noChallengesYet:
      'No challenges yet. Create one to get the community going!',
    joinChallenge: 'Join Challenge',
    howChallengesWorkTitle: 'How Challenges Work',
    howChallengesWorkItem1: 'Post content with the challenge hashtag',
    howChallengesWorkItem2: 'No prizes tied to pet records or identity',
    howChallengesWorkItem3: 'Pure fun and community engagement',
    howChallengesWorkItem4: 'Featured posts get highlighted',
    petParks: 'Pet Parks',
    likesComments: '{{likes}} likes • {{comments}} comments',
    failedAddComment: 'Failed to add comment. Please try again.',
    ownedBy: 'Owned by',
    adjustCropAreaFirst: 'Please adjust the crop area first',
    videoEditingComingSoon:
      'Video editing is coming soon. You can trim in a future update.',
    useAsIs: 'Use as is',
    crop: 'Crop',
    filter: 'Filter',
    saving: 'Saving...',
    aspectRatio: 'Aspect ratio',
    zoom: 'Zoom',
    zoomHint: 'Pinch or scroll to zoom, drag to reposition',
    postsByYouForPet: 'Posts by you for {{name}}',
    alsoSharedToPlatforms:
      'Also shared to {{names}}. CTA link to Petso included.',
    notification: {
      likeMessage: '{{name}} liked your post',
      commentMessage: '{{name}} commented on your post',
      followMessage: '{{name}} started following you',
      followAcceptedMessage: '{{name}} accepted your follow request',
      reactedMessage: '{{name}} reacted',
    },
    profile: {
      selectPet: 'Select pet',
      privacySet: 'Profile privacy set to {{value}}.',
      privacySetForAllPets: 'Profile privacy set to {{value}} for all pets.',
    },
  },
};

const commonItOverrides: JsonObject = {
  petOnboarding: {
    createPetProfile: 'Crea profilo animale',
    buildingDigitalIdentity: "Costruendo l'identita digitale del tuo animale",
    step1: {
      title: 'Identita base',
      subtitle: 'Stabilisci i dettagli principali del tuo animale',
      petName: 'Nome animale',
      petNamePlaceholder: 'Inserisci nome',
      species: 'Specie',
      selectSpecies: 'Seleziona specie',
      dog: 'Cane',
      cat: 'Gatto',
      bird: 'Uccello',
      reptile: 'Rettile',
      other: 'Altro',
      breed: 'Razza',
      breedPlaceholder: 'Inserisci razza o seleziona Meticcio',
      dateOfBirth: 'Data di nascita (opzionale)',
      gender: 'Genere',
      selectGender: 'Seleziona genere',
      male: 'Maschio',
      female: 'Femmina',
      color: 'Colore (opzionale)',
      colorPlaceholder: 'Es. Nero, Bianco, Marrone...',
      country: 'Paese',
      selectCountry: 'Seleziona paese',
    },
  },
  quickAddPets: {
    species: 'Specie',
    breed: 'Razza',
    gender: 'Genere',
  },
  drawer: {
    settings: 'Impostazioni',
    myProfile: 'Il mio profilo',
    myPets: 'I miei animali',
    addPet: 'Aggiungi animale',
    notifications: 'Notifiche',
    enableNotifications: 'Abilita notifiche',
    viewNotifications: 'Visualizza notifiche',
    appearance: 'Aspetto',
    lightMode: 'Chiaro',
    darkMode: 'Scuro',
    systemDefault: 'Predefinito di sistema',
    logOut: 'Disconnetti',
  },
  navigation: {
    overview: 'Panoramica',
    epassport: 'E-Passport',
    documents: 'Documenti',
    familyTree: 'Albero genealogico',
    knowYourPet: 'Conosci il tuo animale',
    petParks: 'Parchi per animali',
    social: 'Social',
    calendar: 'Calendario',
    appointments: 'Appuntamenti',
    bookAppointment: 'Prenota appuntamento',
    rewards: 'Ricompense',
    notifications: 'Notifiche',
  },
  language: {
    selectLanguage: 'Lingua',
  },
  calendar: {
    title: 'Calendario',
    subtitle: 'Gestisci il programma del tuo animale',
    subtitleAppointments:
      'Visualizza e gestisci gli appuntamenti di tutti i tuoi animali',
    day: 'Giorno',
    today: 'Oggi',
    week: 'Settimana',
    month: 'Mese',
    agenda: 'Agenda',
    bookAppointment: 'Prenota appuntamento',
    comingSoon: 'Prossimamente',
    allPets: 'Tutti gli animali',
    addReminder: 'Aggiungi promemoria',
    appointmentDetails: 'Dettagli appuntamento',
    weekdaySun: 'Dom',
    weekdayMon: 'Lun',
    weekdayTue: 'Mar',
    weekdayWed: 'Mer',
    weekdayThu: 'Gio',
    weekdayFri: 'Ven',
    weekdaySat: 'Sab',
    petPlaceholder: 'Animale',
    categoryPlaceholder: 'Categoria',
    allCategories: 'Tutte le categorie',
    vaccination: 'Vaccinazioni',
    healthCheckup: 'Controllo salute',
    medication: 'Farmaci',
    grooming: 'Toelettatura',
    followUp: 'Follow-up',
    documentRenewal: 'Rinnovo documenti',
    custom: 'Personalizzato',
    scheduled: 'Programmato',
    confirmed: 'Confermato',
    completed: 'Completato',
    cancelled: 'Annullato',
    pendingPayment: 'Pagamento in attesa',
    overdue: 'Scaduto',
    dueSoon: 'In scadenza',
    appointmentCancelledSuccess: 'Appuntamento annullato con successo',
    reminderDeleted: 'Promemoria eliminato',
    confirmDeleteReminder: 'Sei sicuro di voler eliminare questo promemoria?',
    fromHealthRecords: 'Da Salute e cure',
    dateLabel: 'Data',
    petLabel: 'Animale',
    overdueMessage:
      'Questo elemento è scaduto. Aggiorna in Salute e cure o E-Passport.',
    dueSoonMessage:
      'In scadenza a breve. Aggiorna in Salute e cure o E-Passport.',
    record: 'Record',
    recordUpdateHint:
      'Aggiorna questo record in Panoramica → Cartelle cliniche o E-Passport. Le modifiche si sincronizzano automaticamente nel Calendario.',
    categoryVaccination: 'Vaccinazione',
    categoryHealthCheckup: 'Controllo salute',
    categoryMedication: 'Farmaci',
    categoryGrooming: 'Toelettatura',
    categoryFollowUp: 'Follow-up',
    categoryDocumentRenewal: 'Rinnovo documenti',
    categoryCustom: 'Personalizzato',
    overdueRecordsNotice:
      '{{count}} record sanitari scaduti - controlla Calendario o Cartelle cliniche',
    dueSoonRecordsNotice:
      '{{count}} record sanitari in scadenza - controlla il Calendario',
    more: 'altri',
    noUpcomingItems: 'Nessun appuntamento o data sanitaria in arrivo',
    insurance: 'Assicurazione',
    minutes: 'minuti',
    professional: 'Professionista',
    location: 'Luogo',
    notes: 'Note',
    cancelAppointment: 'Annulla appuntamento',
    bookingComingSoonDescription:
      'La prenotazione appuntamenti sarà disponibile al lancio di Petso Pro. Resta aggiornato!',
    gotIt: 'Ho capito',
    reminderDetails: 'Dettagli promemoria',
    titleLabel: 'Titolo',
    reminders: 'Promemoria',
    createReminderModal: {
      createReminder: 'Aggiungi promemoria',
      editReminder: 'Modifica promemoria',
      subtitleCreate: 'Crea un nuovo promemoria per il tuo animale',
      subtitleEdit: 'Aggiorna questo promemoria',
      reminderTitle: 'Titolo promemoria',
      reminderTitlePlaceholder: 'es. Controllo annuale, Richiamo rabbia',
      category: 'Categoria',
      selectPet: 'Seleziona animale',
      choosePet: 'Scegli un animale',
      dateLabel: 'Data',
      timeLabel: 'Ora',
      repeat: 'Ripeti',
      repeatOneTime: 'Una sola volta',
      repeatDaily: 'Ogni giorno',
      repeatWeekly: 'Ogni settimana',
      repeatMonthly: 'Ogni mese',
      repeatCustom: 'Personalizzato',
      repeatEvery: 'Ogni',
      day: 'giorno',
      days: 'giorni',
      repeatCustomHint: 'Ogni {{count}} {{unit}}',
      priority: 'Priorità',
      priorityNormal: 'Normale',
      priorityImportant: 'Importante',
      priorityCritical: 'Critica',
      notificationSettings: 'Impostazioni notifiche',
      enableNotifications: 'Abilita notifiche',
      notifyMe: 'Avvisami',
      atTimeOfReminder: 'All’orario del promemoria',
      oneDayBefore: '1 giorno prima',
      threeDaysBefore: '3 giorni prima',
      notesOptional: 'Note (opzionale)',
      notesPlaceholder: 'Aggiungi eventuali note...',
      cancel: 'Annulla',
      saveChanges: 'Salva modifiche',
      createReminderButton: 'Crea promemoria',
      toastTitleRequired: 'Inserisci un titolo per il promemoria',
      toastSelectPet: 'Seleziona un animale',
      toastUpdated: 'Promemoria aggiornato',
      toastCreated: 'Promemoria creato',
      reminderUpdatedNotificationTitle: 'Promemoria aggiornato',
      reminderUpdatedNotificationMessage: '"{{title}}" è stato aggiornato.',
      reminderCreatedNotificationTitle: 'Promemoria creato',
      reminderCreatedNotificationMessage: '"{{title}}" per {{target}}.',
      yourCalendar: 'il tuo calendario',
      yourPet: 'il tuo animale',
      internalReminder: 'Promemoria interno',
      internalReminderDescription: 'Visibile solo a te e al tuo team',
    },
  },
  knowYourPet: {
    title: 'Conosci il tuo animale',
    breedCareLabel: 'Informazioni di cura per la razza {{breedName}}',
    generalCareLabel:
      'Informazioni generali di cura per questo tipo di animale: {{petType}}',
    unrecognizedBadge: 'Info generali',
    generalNoticeTitle:
      'Informazioni generali di cura per questo tipo di animale',
    generalNoticeBody:
      'Queste informazioni si applicano in generale a questo tipo di animale. Le esigenze individuali possono variare.',
    imageAlt: 'Foto di {{breedName}}',
    petImageAlt: 'Foto di {{petName}}',
    values: {
      size: { small: 'Piccola', medium: 'Media', large: 'Grande' },
      energy: { low: 'Bassa', medium: 'Media', high: 'Alta' },
    },
    badges: {
      apartmentFriendly: 'Adatto ad appartamento',
      familyFriendly: 'Adatto alle famiglie',
    },
    sections: {
      overview: 'Panoramica della razza',
      personality: 'Personalità e tratti',
      lifestyle: 'Stile di vita e bisogni di cura',
      health: 'Considerazioni sulla salute',
      preventativeCare: 'Cure preventive (informative)',
      careTips: 'Consigli di cura',
      indicators: 'Indicatori rapidi',
    },
    labels: {
      origin: 'Origine',
      idealEnvironment: 'Ambiente ideale',
      idealOwner: 'Tipo di proprietario ideale',
      exerciseNeeds: 'Bisogni di esercizio',
      spaceRequirements: 'Spazio necessario',
      groomingNeeds: 'Bisogni di toelettatura',
      timeCommitment: 'Impegno di tempo',
      commonHealthRisks: 'Rischi sanitari comuni',
      vetCheckups: 'Controlli di routine',
      lifespan: 'Aspettativa di vita media',
    },
    indicatorsHelp:
      'Questi indicatori sono riepiloghi basati sui dati di razza.',
    indicators: {
      energy: 'Energia',
      grooming: 'Toelettatura',
      healthSensitivity: 'Sensibilità sanitaria',
    },
    careTipsDisclaimer:
      'Indicazioni solo informative — consulta il veterinario per consigli medici.',
    careTips: {
      bestPractices: 'Buone pratiche',
      mistakesToAvoid: 'Errori comuni da evitare',
      practice: {
        '1': 'Mantieni routine costanti (pasti, passeggiate, sonno).',
        '2': 'Usa rinforzo positivo durante l’addestramento.',
        '3': 'Offri acqua fresca e alimentazione bilanciata.',
        '4': 'Monitora cambiamenti in appetito, energia e comportamento.',
      },
      mistake: {
        '1': 'Saltare i controlli veterinari preventivi.',
        '2': 'Sovralimentare o fare poco esercizio.',
        '3': 'Ignorare segnali iniziali di disagio o stress.',
        '4': 'Usare punizioni dure durante l’addestramento.',
      },
    },
    generic: {
      commonHealth: 'Considerazioni sanitarie comuni',
      dog: {
        personality: {
          '1': 'La maggior parte dei cani prospera con compagnia e routine.',
          '2': 'La socializzazione favorisce un comportamento sicuro e amichevole.',
          '3': 'La stimolazione mentale riduce noia e comportamenti indesiderati.',
          '4': 'L’addestramento funziona meglio se costante e basato su premi.',
        },
        lifestyle: {
          '1': 'Offri attività quotidiana adatta a età e forma fisica.',
          '2': 'Mantieni uno spazio sicuro per riposo e tranquillità.',
          '3': 'La toelettatura regolare supporta pelle e mantello.',
          '4': 'Usa attività di arricchimento (fiuto, giochi, puzzle).',
        },
        health: {
          '1': 'Mantieni aggiornati vaccini e prevenzione parassiti.',
          '2': 'Controlla il peso: l’obesità aumenta molti rischi.',
          '3': 'L’igiene dentale aiuta a prevenire dolore e malattie sistemiche.',
          '4': 'Contatta il veterinario se cambiano appetito, feci o energia.',
        },
        preventative: {
          '1': 'Programma controlli veterinari periodici.',
          '2': 'Usa una dieta completa e adatta all’età.',
          '3': 'Garantisci esercizio e addestramento quotidiani.',
          '4': 'Mantieni prevenzione di pulci, zecche e vermi.',
        },
      },
      cat: {
        personality: {
          '1': 'Molti gatti preferiscono ambienti calmi e prevedibili.',
          '2': 'Il gioco favorisce attività sana e legame.',
          '3': 'Graffiare è naturale: fornisci supporti adeguati.',
          '4': 'Introduzioni graduali riducono lo stress nei cambiamenti.',
        },
        lifestyle: {
          '1': 'Offri spazio verticale e nascondigli per comfort.',
          '2': 'Tieni la lettiera pulita e accessibile.',
          '3': 'Proponi gioco e arricchimento ogni giorno.',
          '4': 'La toelettatura varia: il pelo lungo richiede più cura.',
        },
        health: {
          '1': 'Osserva variazioni in alimentazione, idratazione e lettiera.',
          '2': 'La cura dentale supporta la salute nel lungo periodo.',
          '3': 'La gestione del peso riduce rischio di diabete e problemi articolari.',
          '4': 'I controlli annuali aiutano a individuare problemi precocemente.',
        },
        preventative: {
          '1': 'Mantieni i vaccini aggiornati secondo indicazione veterinaria.',
          '2': 'Fornisci dieta bilanciata e acqua fresca.',
          '3': 'Incoraggia il movimento quotidiano attraverso il gioco.',
          '4': 'Usa prevenzione parassiti raccomandata per la tua area.',
        },
      },
    },
    breedDescriptions: {
      beagle:
        'Amichevole e curioso. I Beagle sono cani da caccia di piccola-media taglia con ottimo olfatto e carattere socievole.',
      labradoodle:
        'Energico e amichevole. I Labradoodle sono stati inizialmente allevati come cani da assistenza ipoallergenici.',
      'bernese-mountain-dog':
        'Dolce e tranquillo. I Bovari Bernesi sono cani da lavoro grandi e tricolori con temperamento mite.',
      burmese:
        'Affettuoso e socievole. I gatti Burmese sono orientati alle persone e hanno un mantello setoso.',
    },
    breedDescriptionFallback:
      '{{breedName}} è una razza unica. Consulta il veterinario per consigli di cura personalizzati.',
    traitNames: {
      Loyal: 'Leale',
      Energetic: 'Energico',
      Social: 'Socievole',
      Intelligent: 'Intelligente',
      Vocal: 'Vocale',
      Calm: 'Calmo',
      Gentle: 'Gentile',
      Independent: 'Indipendente',
      Protective: 'Protettivo',
    },
    originTranslations: {
      England: 'Inghilterra',
      'United States': 'Stati Uniti',
      'United Kingdom': 'Regno Unito',
      Switzerland: 'Svizzera',
      Ethiopia: 'Etiopia',
      'Myanmar (Burma)': 'Myanmar (Birmania)',
      Scotland: 'Scozia',
      'Thailand (Siam)': 'Thailandia (Siam)',
      Canada: 'Canada',
      Russia: 'Russia',
      'Iran (Persia)': 'Iran (Persia)',
      'Maine, United States': 'Maine, Stati Uniti',
      Mediterranean: 'Mediterraneo',
      Germany: 'Germania',
      Australia: 'Australia',
      France: 'Francia',
      'Border of England and Scotland': 'Confine tra Inghilterra e Scozia',
      'Scotland, United Kingdom': 'Scozia, Regno Unito',
      'Houses with yards, active families':
        'Case con giardino, famiglie attive',
      'Houses with yards, family homes':
        'Case con giardino, contesti familiari',
      'Active homes with yards': 'Case attive con giardino',
      'Apartments or houses': 'Appartamenti o case',
      'Family homes with space': 'Case familiari con spazio',
      'Rural areas, active homes, farms': 'Zone rurali, case attive, fattorie',
      'Apartments, urban settings, moderate climate':
        'Appartamenti, contesti urbani, clima temperato',
      'Active homes with vertical space': 'Case attive con spazio verticale',
      'Active homes with space': 'Case attive con spazio',
      'Indoor homes, family settings': 'Ambienti interni, contesti familiari',
      'Indoor homes, quiet environments': 'Ambienti interni, tranquilli',
      'Indoor homes, active households': 'Ambienti interni, famiglie attive',
      'Indoor homes, quiet settings': 'Ambienti interni, contesti tranquilli',
      'Spacious homes, indoor/outdoor access':
        'Case spaziose, accesso interno/esterno',
      'Indoor homes, warm environments': 'Ambienti interni, clima caldo',
      'Apartments, any home': 'Appartamenti, qualsiasi casa',
      'Houses with yards, cooler climates':
        'Case con giardino, climi più freschi',
      Families: 'Famiglie',
      'Cooler climates': 'Climi più freschi',
    },
    bestForTranslations: {
      'Active families': 'Famiglie attive',
      'Those with yards': 'Chi ha un giardino',
      'First-time owners': 'Proprietari alla prima esperienza',
      Families: 'Famiglie',
      'Experienced cat owners': 'Proprietari esperti di gatti',
      'Those wanting playful cats': 'Chi cerca gatti giocosi',
      'Very active owners': 'Proprietari molto attivi',
      'Those wanting unique cats': 'Chi cerca gatti particolari',
      'Experienced owners': 'Proprietari esperti',
      'First-time cat owners': 'Primi proprietari di gatti',
      Seniors: 'Senior',
      'Those wanting affectionate cats': 'Chi desidera gatti affettuosi',
      'Quiet households': 'Famiglie tranquille',
      'Busy professionals': 'Professionisti impegnati',
      'Active households': 'Famiglie attive',
      Hunters: 'Cacciatori',
      'Very active individuals': 'Persone molto attive',
      Farmers: 'Agricoltori',
      'Families with children': 'Famiglie con bambini',
      'Apartment dwellers': 'Chi vive in appartamento',
      'Active individuals': 'Persone attive',
      'Very active families': 'Famiglie molto attive',
      'Those with allergies': 'Persone con allergie',
      'Active seniors': 'Senior attivi',
    },
    careTranslations: {
      '1-hour-daily-of-exercise-and-play':
        '1 ora al giorno di esercizio e gioco',
      'weekly-brushing-regular-baths':
        'Spazzolatura settimanale, bagni regolari',
      'yard-preferred-secure-fencing-essential':
        'Giardino consigliato, recinzione sicura essenziale',
      'moderate-to-high-needs-exercise-and-training':
        'Da moderato ad alto: bisogno di esercizio e addestramento',
      'moderate-daily-walks-and-play':
        'Moderato: passeggiate e gioco quotidiani',
      'regular-brushing-heavy-shedding': 'Spazzolatura regolare, forte muta',
      'large-yard-preferred': 'Preferibile un giardino ampio',
      'minimal-weekly-brushing': 'Minimo: spazzolatura settimanale',
      'vertical-space-for-climbing-essential':
        'Spazio verticale per arrampicarsi essenziale',
      'moderate-needs-interaction': 'Moderato: necessita di interazione',
      'very-high-needs-constant-stimulation':
        'Molto alto: necessita di stimolazione costante',
      'needs-space-to-run-and-climb':
        'Ha bisogno di spazio per correre e arrampicarsi',
      'high-very-interactive': 'Alto: molto interattivo',
      'moderate-enjoys-play-sessions': 'Moderato: gradisce sessioni di gioco',
      'weekly-brushing': 'Spazzolatura settimanale',
      'adaptable-to-various-spaces': 'Adattabile a spazi diversi',
      'moderate-enjoys-companionship': 'Moderato: ama la compagnia',
      'low-to-moderate': 'Da basso a moderato',
      'adaptable-to-small-spaces': 'Adattabile a spazi piccoli',
      'low-very-independent': 'Basso: molto indipendente',
      'moderate-enjoys-play': 'Moderato: ama giocare',
      adaptable: 'Adattabile',
      'moderate-loves-attention': 'Moderato: ama l’attenzione',
      'high-very-active': 'Alto: molto attivo',
      'minimal-gentle-weekly-grooming':
        'Minimo: toelettatura delicata settimanale',
      'needs-climbing-spaces': 'Ha bisogno di spazi per arrampicarsi',
      'moderate-to-high-very-interactive':
        'Da moderato ad alto: molto interattivo',
      'every-6-months': 'Ogni 6 mesi',
    },
    healthTranslations: {
      obesity: 'Obesità',
      epilepsy: 'Epilessia',
      'hip-dysplasia': 'Displasia dell’anca',
      'eye-problems': 'Problemi oculari',
      'weight-management': 'Gestione del peso',
      'regular-exercise': 'Esercizio regolare',
      'eye-exams': 'Controlli oculistici',
      'proper-diet': 'Dieta corretta',
      'regular-vet-visits': 'Visite veterinarie regolari',
      'joint-care': 'Cura delle articolazioni',
      'dental-care': 'Cura dentale',
      'regular-checkups': 'Controlli regolari',
      'heart-disease': 'Malattie cardiache',
      'ear-infections': 'Infezioni alle orecchie',
      'skin-issues': 'Problemi cutanei',
      'dental-problems': 'Problemi dentali',
      'skin-care': 'Cura della pelle',
      'dental-hygiene': 'Igiene dentale',
      'kidney-disease': 'Malattia renale',
      diabetes: 'Diabete',
      bloat: 'Torsione gastrica',
      cancer: 'Cancro',
      'elbow-dysplasia': 'Displasia del gomito',
      'dental-issues': 'Problemi dentali',
      'skin-allergies': 'Allergie cutanee',
      'breathing-issues': 'Problemi respiratori',
      'eye-conditions': 'Patologie oculari',
      'bladder-stones': 'Calcoli vescicali',
      'joint-issues': 'Problemi articolari',
      'respiratory-issues': 'Problemi respiratori',
      'patellar-luxation': 'Lussazione della rotula',
      allergies: 'Allergie',
      deafness: 'Sordità',
      'vet-checkups': 'Controlli veterinari',
    },
    lifespanTranslations: {
      '13-16-years': '13-16 anni',
      '12-15-years': '12-15 anni',
      '12-16-years': '12-16 anni',
      '12-17-years': '12-17 anni',
      '9-15-years': '9-15 anni',
      '7-10-years': '7-10 anni',
      '15-20-years': '15-20 anni',
      '11-14-years': '11-14 anni',
      '8-14-years': '8-14 anni',
      '10-12-years': '10-12 anni',
      '10-13-years': '10-13 anni',
      '10-15-years': '10-15 anni',
      '12-14-years': '12-14 anni',
      '14-16-years': '14-16 anni',
    },
    frequencyTranslations: {
      annually: 'Annuale',
      'every-6-months': 'Ogni 6 mesi',
    },
    yearsTranslations: {
      years: 'anni',
    },
    toast: {
      selectPetType: 'Seleziona un tipo di animale per usare questa funzione.',
    },
  },
  features: {
    qrHub: {
      title: 'Condivisione e accesso animale',
      subtitle: 'Controllo accessi QR sicuro con Solana',
      createQR: 'Crea QR',
      scanQR: 'Scansiona QR',
      accessHistory: 'Cronologia accessi',
      activeQRCodes: 'Codici QR attivi',
      totalScans: 'Scansioni totali',
      expired: 'Scaduto',
      revoked: 'Revocato',
      createNewQR: 'Crea nuovo QR',
      noActiveQRCodes: 'Nessun codice QR attivo',
      createYourFirstQRCode: 'Crea il tuo primo codice QR',
      inactiveQRCodes: 'Codici QR inattivi',
      blockchainTitle: 'Controllo accessi protetto da blockchain',
      blockchainDesc:
        'Gli accessi vengono registrati su Solana. Puoi revocare o controllare la cronologia in qualsiasi momento.',
      realTimeNotifications: 'Notifiche di accesso in tempo reale',
      instantRevocation: 'Revoca immediata',
      completeAuditTrail: 'Audit trail completo',
      encryptedTransmission: 'Trasmissione dati cifrata',
      qrCodeLabel: 'Codice QR',
      petLabel: 'Animale:',
      accessTypeLabel: 'Tipo accesso:',
      expiresLabel: 'Scade:',
      shareQRAccess: 'Condividi accesso QR',
      shareLinkLabel: 'Link di condivisione',
      shareVia: 'Condividi tramite',
      email: 'Email',
      sms: 'SMS',
      downloadStarted: 'Download codice QR avviato',
      downloadImageStarted: 'Download immagine QR avviato',
      linkCopied: 'Link copiato negli appunti!',
      shareWarning:
        '⚠️ Questo link concede accesso ai dati di {{petName}}. Condividilo solo con persone fidate.',
      createFlow: {
        title: 'Crea nuovo QR',
        subtitle: 'Genera un QR sicuro per accesso e condivisione',
        stepSelectPets: 'Seleziona animali',
        stepAccessType: 'Tipo accesso',
        stepDataScope: 'Ambito dati',
        stepDuration: 'Durata',
        stepGenerateQR: 'Genera QR',
        selectPetProfiles: 'Seleziona profili animale',
        choosePetsToGrant: 'Scegli a quali animali concedere accesso',
        noPetProfilesFound: 'Nessun profilo animale trovato',
        chooseAccessType: 'Scegli tipo di accesso',
        selectAccessLevel: "Seleziona il livello d'accesso",
        selectDataScope: 'Seleziona ambito dati',
        chooseWhatToShare: 'Scegli quali dati condividere',
        ownershipTransferNote:
          'Trasferimento proprietà richiede accesso completo a tutti i moduli dati.',
        accessDuration: "Durata dell'accesso",
        setHowLongValid: 'Imposta per quanto tempo sarà valido',
        durationLockedNote:
          'Per trasferimento proprietà, la durata è impostata su revoca manuale.',
        timeLimited: 'Limitato nel tempo',
        duration1h: '1 ora',
        duration4h: '4 ore',
        duration8h: '8 ore',
        duration24h: '24 ore',
        duration3d: '3 giorni',
        duration1w: '1 settimana',
        eventBased: 'Basato su evento',
        eventBasedDesc: "Scade al termine dell'evento",
        manualRevocationRecommended: 'Revoca manuale (consigliato)',
        untilManuallyRevoked: 'Valido fino a revoca manuale',
        ownershipRevocationDesc:
          'Mantieni attivo finché il nuovo proprietario completa la procedura.',
        manualRevocationDesc:
          'Resta valido finché non viene revocato manualmente.',
        generatingQR: 'Generazione QR...',
        qrCodeGenerated: 'Codice QR generato',
        shareCodeToGrant: "Condividi questo codice per concedere l'accesso",
        petsLabel: 'Animali',
        noneSelected: 'Nessuno selezionato',
        dataScopeLabel: 'Ambito dati',
        modulesCount: '{{count}} moduli',
        durationLabel: 'Durata',
        afterEventCompletes: "Dopo il completamento dell'evento",
        untilManuallyRevokedShort: 'Fino a revoca manuale',
        downloadQR: 'Scarica QR',
        shareLink: 'Condividi link',
        print: 'Stampa',
        revokeWarning: 'Puoi revocare questo QR in qualsiasi momento.',
        back: 'Indietro',
        next: 'Avanti',
        complete: 'Completa',
        viewOnly: 'Solo visualizzazione',
        viewOnlyDesc: 'Può solo visualizzare i dati consentiti',
        professionalUpdate: 'Aggiornamento professionale',
        professionalUpdateDesc:
          'Professionista può aggiornare i record autorizzati',
        temporaryCare: 'Cura temporanea',
        temporaryCareDesc: 'Accesso temporaneo per caregiver',
        emergencyAccess: 'Accesso di emergenza',
        emergencyAccessDesc: 'Accesso rapido alle info essenziali',
        ownershipTransfer: 'Trasferimento proprietà',
        ownershipTransferDesc: 'Condivisione completa per passaggio proprietà',
        scopeIdentity: 'Identità',
        scopeIdentityDesc: 'Profilo base e identificativi',
        scopeHealth: 'Salute',
        scopeHealthDesc: 'Dati sanitari principali',
        scopePhysical: 'Metriche fisiche',
        scopePhysicalDesc: 'Peso, altezza e condizioni',
        scopeMedical: 'Aggiornamenti medici',
        scopeMedicalDesc: 'Note e prescrizioni',
        scopeGrooming: 'Toelettatura',
        scopeGroomingDesc: 'Storico cura e toelettatura',
        scopeBehavioral: 'Comportamento',
        scopeBehavioralDesc: 'Osservazioni comportamentali',
        scopeDocuments: 'Documenti',
        scopeDocumentsDesc: 'File e certificati caricati',
        scopeFamily: 'Famiglia',
        scopeFamilyDesc: 'Informazioni genealogiche',
        scopeAppointments: 'Appuntamenti',
        scopeAppointmentsDesc: 'Storico e prossimi appuntamenti',
        scopePayment: 'Pagamenti',
        scopePaymentDesc: 'Dettagli pagamento correlati',
        scopeAIInsights: 'Insight IA',
        scopeAIInsightsDesc: 'Insight generati da IA',
        microchip: 'Microchip',
        microchipDesc: 'Informazioni e stato microchip',
      },
    },
    aiConsent: {
      title: 'Attiva insight IA salute',
      selectPetsBelow: 'Seleziona animali qui sotto',
      forPet: 'per {{name}}',
      forPetsCount: 'per {{count}} animali',
      stepConsent: 'Consenso',
      stepDidLink: 'Collegamento DID',
      stepSharing: 'Condivisione',
      whatAIDoes: "Cosa fa l'IA salute",
      does1: 'Analizza peso, crescita e condizione corporea nel tempo',
      does2: 'Mostra scadenze vaccinali e promemoria preventivi',
      does3: 'Riassume pattern di cura e attività (non diagnostico)',
      does4:
        'Evidenzia indicatori di rischio e suggerisce quando consultare il veterinario',
      whatAIDoesNot: "Cosa NON fa l'IA salute",
      notDoes1: 'Non effettua diagnosi né sostituisce il veterinario',
      notDoes2: 'Non condivide dati sanitari senza consenso',
      notDoes3: 'Non prescrive trattamenti o farmaci',
      notDoes4: 'Non archivia dati fuori dal tuo controllo (collegato al DID)',
      dataSourcesUsed: 'Fonti dati utilizzate',
      petProfileData: 'Dati profilo animale',
      healthRecords: 'Cartelle cliniche',
      vaccinationHistory: 'Storico vaccinazioni',
      groomingActivity: 'Toelettatura e attività',
      privacyControlTitle: 'Privacy e controllo',
      privacyControlDesc:
        'Controlli tu chi può vedere gli insight IA. Puoi disattivarli in qualsiasi momento.',
      selectPets: 'Seleziona animale/i per consenso IA',
      choosePetsDesc: 'Scegli per quali animali attivare insight IA salute',
      noPetsFound: 'Nessun animale trovato. Aggiungine uno prima.',
      selectPetsPlaceholder: 'Seleziona animale/i...',
      petsSelectedCount: '{{count}} animali selezionati',
      selectAll: 'Seleziona tutti',
      iUnderstandEnable: 'Comprendo e attivo gli insight IA salute per',
      selectedPets: 'animali selezionati',
      aiAnalyzeNotice:
        "L'IA analizzerà trend di salute e fornirà insight non diagnostici.",
      cancel: 'Annulla',
      continue: 'Continua',
      didLinkedInsights: 'Insight IA collegati al DID',
      didLinkedDesc:
        "Gli insight IA sono collegati all'identità decentralizzata (DID) dell'animale.",
      verified: 'Verificato',
      noDidLinked: 'Nessun DID collegato',
      benefitsTitle: 'Vantaggi degli insight collegati al DID',
      benefit1:
        "Gli stessi insight seguono l'animale tra veterinari e piattaforme",
      benefit2: 'Scegli tu chi vede cosa',
      benefit3: 'Revoca o modifica la condivisione senza perdere la cronologia',
      benefit4:
        'Privacy: le prove possono essere verificate senza esporre i dati',
      back: 'Indietro',
      sharingOptionsTitle: 'Opzioni di condivisione (modificabili dopo)',
      sharingOptionFull: 'Insight completi',
      sharingOptionSummary: 'Solo riepilogo',
      sharingOptionZkp: 'Solo badge ZKP verificati',
      sharingOptionNothing: 'Niente (visibili solo a te)',
      currentSelection: 'Selezione attuale:',
      fullInsights: 'Insight completi',
      nothing: 'Niente',
      activateInsights: 'Attiva insight IA',
    },
    aiInsights: {
      title: 'Insight IA salute',
      aiInsights: 'Insight IA',
      notActivated: 'Insight IA salute non attivati',
      enableForPet:
        'Attiva gli insight IA salute per {{name}} nel flusso di consenso IA.',
      goBack: 'Torna indietro',
      allInsights: 'Tutti gli insight',
      growthWeight: 'Crescita e peso',
      preventiveCare: 'Cure preventive',
      behavioral: 'Comportamento',
      lifestyle: 'Stile di vita',
      riskIndicators: 'Indicatori di rischio',
      overallHealthTrend: 'Andamento generale salute',
      improving: 'In miglioramento',
      stable: 'Stabile',
      needsAttention: 'Richiede attenzione',
      lastUpdated: 'Ultimo aggiornamento',
      refreshInsights: 'Aggiorna insight',
      actionNeeded: 'Azione necessaria',
      totalInsights: 'Insight totali',
      weightTrendTitle: 'Andamento peso (ultimi 6 mesi)',
      confidenceHigh: 'Alta affidabilita',
      confidenceMedium: 'Affidabilita media',
      confidenceLow: 'Bassa affidabilita',
      dataUsedForInsight: 'Dati usati per questo insight',
      suggestionLabel: 'Suggerimento',
      updatedLabel: 'Aggiornato',
      helpfulQuestion: 'Utile?',
      lowConfidenceWarning:
        'Questo insight ha affidabilita piu bassa a causa di dati limitati. Valuta un consulto con il veterinario per una valutazione professionale.',
      insightHealthyWeightTitle: 'Intervallo di peso sano',
      insightHealthyWeightSummary:
        'Il peso rientra in un intervallo sano per razza ed eta.',
      insightHealthyWeightDetails:
        'In base al registro pesi e agli standard di razza, il peso del tuo animale e in un intervallo salutare. Un monitoraggio regolare aiuta a mantenere una condizione ottimale.',
      insightHealthyWeightDataPoints: [
        'Registro pesi (6 voci)',
        'Standard di razza',
        "Intervalli adeguati all'eta",
      ],
      insightHealthyWeightAction:
        'Continua con alimentazione ed esercizio attuali. Pesa ogni mese e annota eventuali cambi improvvisi.',
      insightVaccinationTitle: 'Vaccinazioni e cure preventive',
      insightVaccinationSummary:
        'Calendario vaccinale e cure preventive sono aggiornati.',
      insightVaccinationDetails:
        'Vaccini principali e trattamenti preventivi sono registrati e allineati ai calendari comuni. Richiami e scadenze sono tracciati.',
      insightVaccinationDataPoints: [
        'Record vaccinazioni (4 vaccini)',
        'Storico visite veterinarie',
        "Protocolli basati sull'eta",
      ],
      insightVaccinationAction:
        'Continua a registrare richiami e controlli annuali. Imposta promemoria per le prossime scadenze.',
      insightGroomingTitle: 'Toelettatura e cura del mantello',
      insightGroomingSummary:
        'Visite di toelettatura e condizioni del mantello sono monitorate.',
      insightGroomingDetails:
        'La cronologia toelettatura e le note sul mantello aiutano a individuare cambiamenti nel tempo. Utile per problematiche cutanee o del pelo e per il benessere generale.',
      insightGroomingDataPoints: [
        'Record toelettatura (4 sessioni)',
        'Note professionali di gestione',
        'Osservazioni comportamentali',
      ],
      insightGroomingAction:
        'Aggiungi note del toelettatore quando possibile. Segnala eventuali cambi di pelle o mantello.',
      insightActivityTitle: 'Attivita e stile di vita',
      insightActivitySummary:
        'I pattern di attivita supportano uno stile di vita sano.',
      insightActivityDetails:
        'I pattern di attivita ed esercizio sono coerenti. Positivo per peso e comportamento. Combina con dati su peso e veterinario per un quadro completo.',
      insightActivityDataPoints: [
        'Storico appuntamenti (12 visite)',
        'Analisi frequenza visite',
        'Pattern di benessere',
      ],
      insightActivityAction:
        "Continua a registrare passeggiate e gioco. Aumenta l'attivita se il peso tende a salire.",
      insightDentalTitle: 'Cura dentale e orale',
      insightDentalSummary:
        'La cura dentale e monitorata; valuta controllo veterinario se necessario.',
      insightDentalDetails:
        'Note dentali ed eventuali raccomandazioni veterinarie sono registrate. Controlli regolari aiutano a prevenire patologie dentali.',
      insightDentalDataPoints: [
        'Ultima visita dentale (11 mesi fa)',
        'Fattori di rischio dentale di razza',
        'Considerazioni legate all eta',
      ],
      insightDentalAction:
        'Spazzola i denti se consigliato. Prenota un controllo dentale con il veterinario se in ritardo.',
      noInsightsInCategory: 'Nessun insight disponibile in questa categoria.',
      checkBackAfterData: 'Riprova quando saranno disponibili più dati.',
      medicalDisclaimer:
        'Gli insight IA salute sono solo informativi e non sostituiscono il veterinario.',
      medicalDisclaimerLabel: 'Avviso medico',
      sharingSettings: 'Impostazioni di condivisione',
    },
    healthRecords: {
      title: 'Cartelle cliniche e cura',
      grooming: 'Toelettatura',
      medicalUpdates: 'Aggiornamenti medici',
      insurance: 'Assicurazione',
      documents: 'Documenti',
      immutableLocked: 'Immutabile (bloccato)',
      immutableFields: 'Nome, Specie, Data nascita, DID',
      amendableContract: 'Modificabile (basato su contratto)',
      amendableFields: 'Peso, Vaccinazioni, Medico, Toelettatura',
      ownerBadge: 'Proprietario',
      vetBadge: 'Veterinario',
      groomerBadge: 'Toelettatore',
      verifiedBadge: 'Verificato',
      expired: 'Scaduto',
      dueSoon: 'In scadenza',
      vaccinationCurrent: 'Aggiornato',
      loadingVaccinationRecords: 'Caricamento registrazioni vaccinazioni...',
      failedToLoadVaccinations: 'Caricamento vaccinazioni non riuscito',
      upcomingBoosters: 'Richiami in arrivo',
      boosterDueDate: 'Scadenza: {{date}}',
      noUpcomingBoosters: 'Nessun richiamo nei prossimi 60 giorni',
      administeredDate: 'Somministrato: {{date}}',
      administeredByClinic: 'Da: {{name}} - {{clinic}}',
      batchLabel: 'Lotto: {{number}}',
      expiresDate: 'Scade: {{date}}',
      onChainCertified: 'Certificato on-chain',
      transactionSignatureSolana: 'Firma transazione (Solana)',
      viewOnSolana: 'Visualizza su Solana',
      documentHashSolana: 'Hash documento (Solana)',
      notesLabel: 'Note',
      attachCertificate: 'Allega certificato',
      issueCredential: 'Emetti credenziale',
      smartContractAuditTrail: 'Registro audit smart contract',
      auditTrailDesc:
        'Quando i veterinari emettono credenziali, una prova crittografica viene archiviata sulla blockchain Solana, rendendo il registro vaccinale verificabile indipendentemente da terze parti.',
      vaccinationRecorded: 'Vaccinazione {{vaccineName}} registrata',
      onChainCredentialCount:
        '{{count}} di {{total}} vaccinazioni hanno credenziali on-chain',
      loadingPhysicalMetrics: 'Caricamento metriche fisiche...',
      failedToLoadPhysicalMetrics: 'Caricamento metriche non riuscito.',
      tryAgain: 'Riprova',
      physicalMetrics: 'Metriche fisiche',
      physicalMetricsDesc: 'Monitora peso, altezza e condizione corporea',
      addMetric: 'Aggiungi metrica',
      noPhysicalMetricsRecorded: 'Nessuna metrica fisica registrata',
      addFirstMetric: 'Aggiungi la prima metrica',
      groomingRecordsTitle: 'Storico toelettatura',
      groomingDesc: 'Gestisci i record di toelettatura e cura',
      recordAddedTitle: 'Record salute aggiunto',
      groomingRecordAddedMessage:
        'Record toelettatura ({{serviceType}}) aggiunto per {{petName}}.',
      failedToLoadGroomingRecords:
        'Caricamento record toelettatura non riuscito',
      groomerVerified: 'Toelettatore verificato',
      servicesProvided: 'Servizi forniti',
      coatConditionLabel: 'Condizione mantello',
      behavioralObservationsLabel: 'Osservazioni comportamentali',
      groomerNotesLabel: 'Note del toelettatore',
      verifiedOnSolana: 'Verificato su Solana',
      awaitingBlockchainSync: 'In attesa sincronizzazione blockchain',
      errors: {
        addGroomingFailed:
          'Aggiunta record toelettatura non riuscita. Riprova.',
        addVaccinationFailed: 'Aggiunta vaccinazione non riuscita. Riprova.',
      },
      attachPhotos: 'Allega foto',
      viewDocument: 'Visualizza documento',
      whyGroomingMatters: 'Perché i record di toelettatura sono importanti',
      whyGroomingBody:
        'I record di toelettatura aiutano a monitorare mantello, comportamento e benessere generale nel tempo.',
      addRecord: 'Aggiungi record',
      noGroomingYet: 'Nessun record di toelettatura',
      addFirstGrooming: 'Aggiungi il primo record toelettatura',
      vaccinations: 'Vaccinazioni',
      vaccinationsDesc: 'Storico vaccinazioni e prossime scadenze',
      addVaccination: 'Aggiungi vaccinazione',
      noVaccinationYet: 'Nessun record vaccinale',
      addFirstVaccination: 'Aggiungi la prima vaccinazione',
      insuranceTitle: 'Assicurazione',
      insuranceDesc: 'Gestisci documenti assicurativi sanitari e viaggio',
      recordUpdatedTitle: 'Record salute aggiornato',
      travelInsuranceUpdated: 'Assicurazione viaggio aggiornata',
      healthInsuranceUpdated: 'Assicurazione salute aggiornata',
      travelInsuranceUpdatedMessage:
        "L'assicurazione viaggio è stata aggiornata per {{petName}}.",
      healthInsuranceUpdatedMessage:
        "L'assicurazione salute è stata aggiornata per {{petName}}.",
      insuranceDocumentAddedMessage:
        'Documento assicurativo {{type}} aggiunto per {{petName}}.',
      saveFailed: 'Salvataggio non riuscito. Riprova.',
      travelInsurance: 'Assicurazione viaggio',
      healthInsurance: 'Assicurazione salute',
      addDetails: 'Aggiungi dettagli',
      noTravelInsuranceYet: 'Nessuna assicurazione viaggio',
      noHealthInsuranceYet: 'Nessuna assicurazione salute',
      insuranceFields: {
        provider: 'Fornitore',
        policyNumber: 'Numero polizza',
        coverage: 'Copertura',
        emergency: 'Emergenza',
        details: 'Dettagli',
        type: 'Tipo',
        deductible: 'Franchigia',
        recorded: 'Registrato',
      },
      insuranceModal: {
        travelTitle: 'Assicurazione viaggio',
        healthTitle: 'Assicurazione salute',
        provider: 'Fornitore',
        policyNumber: 'Numero polizza',
        coverageStart: 'Inizio copertura',
        coverageEnd: 'Fine copertura',
        emergencyContact: 'Contatto di emergenza',
        coverageDetailsOptional: 'Dettagli copertura (opzionale)',
        coverageType: 'Tipo copertura',
        selectType: 'Seleziona tipo...',
        deductibleOptional: 'Franchigia (opzionale)',
        providerTravelPlaceholder: 'es. PetTravel Insure',
        providerHealthPlaceholder: 'es. PetCare Health',
        policyTravelPlaceholder: 'es. PTI-123456',
        policyHealthPlaceholder: 'es. PCH-789012',
        emergencyContactPlaceholder: 'Telefono o email per emergenze',
        coverageDetailsPlaceholder: 'Note copertura aggiuntive...',
        deductiblePlaceholder: 'es. 250€/anno',
        typeAccidentOnly: 'Solo infortunio',
        typeAccidentIllness: 'Infortunio e malattia',
        typeWellness: 'Benessere',
        typeComprehensive: 'Completa',
        cancel: 'Annulla',
        save: 'Salva',
      },
      insuranceDocuments: {
        travelDocument: 'Documento assicurazione viaggio',
        healthDocument: 'Documento assicurazione salute',
        view: 'Visualizza',
        replace: 'Sostituisci',
        remove: 'Rimuovi',
        upload: 'Carica',
      },
      medicalUpdatesTitle: 'Aggiornamenti medici',
      medicalUpdatesDesc: 'Annota diagnosi, terapie e progressi',
      addUpdate: 'Aggiungi aggiornamento',
      noMedicalUpdatesYet: 'Nessun aggiornamento medico',
      addFirstMedicalUpdate: 'Aggiungi il primo aggiornamento medico',
      addMetricModal: {
        title: 'Aggiungi metrica',
        metricType: 'Tipo metrica',
        weight: 'Peso',
        heightLength: 'Altezza / lunghezza',
        bodyConditionScore: 'Punteggio condizione corporea',
        valueKg: 'Valore (kg)',
        valueCm: 'Valore (cm)',
        value19: 'Valore (1-9)',
        date: 'Data',
        notesOptional: 'Note (opzionale)',
        notesPlaceholder: 'Aggiungi note facoltative...',
        blockchainNotice: 'Questa modifica verrà registrata su blockchain',
        cancel: 'Annulla',
        saving: 'Salvataggio...',
        addMetric: 'Aggiungi metrica',
      },
      addVaccinationModal: {
        title: 'Aggiungi record vaccinazione',
        subtitle: 'Registra una vaccinazione per questo animale',
        vaccineName: 'Nome vaccino',
        selectVaccine: 'Seleziona vaccino',
        customVaccineName: 'Nome vaccino personalizzato',
        customVaccineHint:
          'Inserisci il nome completo del vaccino se non è presente in elenco',
        dateAdministered: 'Data somministrazione',
        expiryDate: 'Data scadenza',
        boosterDueOptional: 'Data richiamo (opzionale)',
        administeredBy: 'Somministrato da',
        clinicName: 'Nome clinica',
        batchNumber: 'Numero lotto',
        notesOptional: 'Note (opzionale)',
        administeredByPlaceholder: 'Nome veterinario o clinica',
        clinicNamePlaceholder: 'Nome clinica o studio',
        batchPlaceholder: 'Numero batch o lotto',
        notesPlaceholder: 'Note aggiuntive',
        blockchainNotice: 'La vaccinazione verrà registrata su blockchain.',
        cancel: 'Annulla',
        addVaccination: 'Aggiungi vaccinazione',
      },
      addMedicalUpdateModal: {
        title: 'Aggiungi aggiornamento medico',
        subtitle: 'Registra un nuovo aggiornamento sanitario',
        updateType: 'Tipo aggiornamento',
        diagnosis: 'Diagnosi',
        treatment: 'Trattamento',
        prescription: 'Prescrizione',
        recovery: 'Recupero',
        titleLabel: 'Titolo',
        titlePlaceholder: 'es. Controllo annuale',
        date: 'Data',
        veterinarian: 'Veterinario',
        clinicName: 'Nome clinica',
        vetPlaceholder: 'Nome veterinario',
        clinicPlaceholder: 'Nome clinica o studio',
        description: 'Descrizione',
        descriptionPlaceholder: "Descrivi l'aggiornamento...",
        medicationsOptional: 'Farmaci (opzionale)',
        medicationsPlaceholder: 'Elenca eventuali farmaci',
        medicationsHint: 'Inserisci ogni farmaco su una nuova riga',
        status: 'Stato',
        active: 'Attivo',
        ongoing: 'In corso',
        resolved: 'Risolto',
        followUpOptional: 'Data follow-up (opzionale)',
        medicalNotice:
          'Gli aggiornamenti medici vengono aggiunti al contratto smart modificabile con audit trail completo. Nulla viene sovrascritto: tutta la cronologia è preservata.',
        cancel: 'Annulla',
        addUpdate: 'Aggiungi aggiornamento',
      },
      addGroomingModal: {
        title: 'Aggiungi record toelettatura',
        subtitle: 'Registra un servizio di toelettatura',
        serviceType: 'Tipo servizio',
        selectServiceType: 'Seleziona tipo servizio',
        serviceDate: 'Data servizio',
        groomerName: 'Nome toelettatore',
        salonClinicName: 'Nome salone/clinica',
        groomerPlaceholder: 'Nome del toelettatore',
        salonPlaceholder: 'Nome salone o clinica',
        servicesProvided: 'Servizi forniti',
        selectOneService: 'Seleziona almeno un servizio',
        coatCondition: 'Condizione mantello',
        coatPlaceholder: 'es. Buono, secco, infeltrito',
        behavioralObservations: 'Osservazioni comportamentali',
        behavioralPlaceholder:
          'es. Calmo e collaborativo durante tutta la sessione',
        groomerNotes: 'Note del toelettatore',
        groomerNotesPlaceholder:
          'Osservazioni professionali, raccomandazioni e note di cura...',
        groomingNotice:
          'Queste informazioni aiutano a tracciare benessere e routine di cura.',
        cancel: 'Annulla',
        addRecord: 'Aggiungi record',
        bath: 'Bagno',
        haircut: 'Taglio',
        nailTrim: 'Taglio unghie',
        earCleaning: 'Pulizia orecchie',
        brush: 'Spazzolatura',
        deshedding: 'Rimozione sottopelo',
        teethBrushing: 'Spazzolatura denti',
        analGlandExpression: 'Spremitura ghiandole anali',
        fullGrooming: 'Servizio toelettatura completo',
        basicBathBrush: 'Bagno e spazzolatura base',
        nailTrimOnly: 'Solo taglio unghie',
        desheddingTreatment: 'Trattamento rimozione sottopelo',
        puppyFirstGroom: 'Prima toelettatura cucciolo/gattino',
        other: 'Altro',
      },
    },
  },
  epassport: {
    pageTitle: 'E-Passport',
    pageSubtitle: 'Documento di viaggio digitale per {{name}}',
    viewPassport: 'Visualizza passaporto',
    travelCheck: 'Controllo viaggio',
    share: 'Condividi',
    qrCode: 'Codice QR',
    exportPdf: 'Esporta PDF',
    exportButton: 'Esporta',
    printButton: 'Stampa',
    status: {
      complete: 'Completato',
      incomplete: 'Incompleto',
      notCreated: 'Non creato',
    },
    sectionsComplete: '{{completed}}/{{total}} sezioni completate',
    requiredSections: '{{required}}/{{total}} sezioni obbligatorie',
    sectionsTitle: 'Sezioni E-Passport',
    importantDisclaimer:
      'I requisiti di viaggio possono variare per paese e periodo. Verifica sempre con le autorita ufficiali prima della partenza.',
    petIdentification: 'Identificazione animale',
    ownerInformation: 'Informazioni proprietario',
    microchipInformation: 'Informazioni microchip',
    vaccinationRecords: 'Record vaccinazioni',
    healthCertificatesTests: 'Certificati sanitari e test',
    travelBorderRequirements: 'Requisiti di viaggio e frontiera',
    uploadPassportDocument: 'Carica documento passaporto',
    additionalPassportDocumentLabel: 'Documento passaporto aggiuntivo',
    passportDocumentLabel: 'Documento passaporto',
    newDocumentLabel: 'Nuovo documento',
    viewPassportTitle: 'Visualizza passaporto',
    digitalTravelIdDocument: 'Documento digitale di identificazione viaggio',
    statusLabel: 'Stato',
    viewPassportStatusComplete: 'Completato',
    viewPassportStatusIncomplete: 'Incompleto',
    viewPassportStatusNotCreated: 'Non creato',
    fieldPetName: 'Nome animale',
    fieldSpecies: 'Specie',
    fieldBreed: 'Razza',
    fieldDateOfBirth: 'Data di nascita',
    fieldGender: 'Genere',
    fieldColor: 'Colore',
    fieldColorMarkings: 'Colore/segni distintivi',
    fieldMicrochipId: 'ID microchip',
    fieldMicrochipNumber: 'Numero microchip',
    fieldMicrochipStandard: 'Standard microchip',
    fieldImplantDate: 'Data di impianto',
    fieldImplantLocation: 'Posizione di impianto',
    fieldDid: 'DID',
    fieldOwnerName: 'Nome proprietario',
    fieldOwnerNameLabel: 'Nome proprietario',
    fieldOwnerDid: 'DID proprietario',
    fieldOwnerDidLabel: 'DID proprietario',
    fieldContactEmail: 'Email di contatto',
    fieldContactPhone: 'Telefono di contatto',
    fieldAddress: 'Indirizzo',
    addAnotherDocument: 'Aggiungi un altro documento',
    noPassportDocumentUploadedYet: 'Nessun documento passaporto caricato',
    uploadPassport: 'Carica passaporto',
    travelComplianceSummary: 'Riepilogo conformita viaggio',
    compliant: 'Conforme',
    noDestinationsCheckedYet: 'Nessuna destinazione verificata',
    actionRequired: 'Azione richiesta',
    useTravelCheckToAddDestinations:
      'Usa Controllo viaggio per aggiungere destinazioni',
    notCompliant: 'Non conforme',
    completeRequirementsFirst: 'Completa prima i requisiti richiesti',
    documentSections: 'Sezioni documento',
    sectionItemsCount: '{{items}}/{{total}} elementi',
    passportSecuredBlockchain:
      'Questo passaporto di {{name}} e protetto con verifica blockchain.',
    generated: 'Generato:',
    backToDashboard: 'Torna alla dashboard',
    recommended: 'Consigliato',
    uploaded: 'Caricato',
    notUploaded: 'Non caricato',
    fullPassportDocumentCount: '{{count}} documento passaporto caricato',
    fullPassportDocumentNoDocs: 'Nessun documento passaporto caricato',
    viewReplace: 'Visualizza / Sostituisci',
    required: 'Obbligatorio',
    complete: 'Completo',
    selectDestinationToCheck: 'Seleziona una destinazione da verificare',
    vaccinationsRecorded: '{{count}} vaccinazioni registrate',
    medicalUpdatesRecorded: '{{count}} aggiornamenti medici registrati',
    itemsCompleted: '{{items}}/{{total}} elementi completati',
    upcomingExpiries: 'Scadenze imminenti',
    upcomingExpiriesDesc:
      'Monitora documenti, vaccini e certificati in scadenza.',
    privacySecurity: 'Privacy e sicurezza',
    privacyBullet1: 'Condividi solo le sezioni necessarie',
    privacyBullet2: 'I link possono essere limitati nel tempo',
    privacyBullet3: 'Revoca accesso in qualsiasi momento',
    privacyBullet4: 'I file grezzi non sono inclusi nel QR',
    privacyBullet5: 'Controllo verificabile dei dati condivisi',
    travelReadinessCheck: 'Controllo idoneita viaggio',
    verifyComplianceDestination: 'Verifica conformita per la tua destinazione',
    selectDestinationCountry: 'Seleziona paese di destinazione',
    chooseCountry: 'Scegli un paese',
    importantNotes: 'Note importanti',
    selectDestinationToView:
      'Seleziona una destinazione per vedere i requisiti',
    shareEpassportTitle: 'Condividi E-Passport',
    shareEpassportSubtitle:
      'Condividi in modo sicuro il passaporto di {{name}}',
    shareWith: 'Condividi con',
    selectRecipientType: 'Seleziona tipo destinatario',
    recipientAirline: 'Compagnia aerea',
    recipientVet: 'Veterinario',
    recipientBorder: 'Autorita di frontiera',
    recipientHotel: 'Hotel',
    recipientRelocation: 'Servizio trasferimento animali',
    sectionsToShare: 'Sezioni da condividere',
    fullEpassport: 'E-Passport completo',
    selectedSectionsOnly: 'Solo sezioni selezionate',
    zkpVerificationOnly: 'Solo verifica ZKP',
    accessDuration: "Durata dell'accesso",
    duration24h: '24 ore',
    duration7d: '7 giorni',
    duration30d: '30 giorni',
    untilManuallyRevoked: 'Fino a revoca manuale',
    accessRevokeNote:
      'Puoi revocare questo accesso in qualsiasi momento dalle impostazioni di condivisione.',
    generateShareLink: 'Genera link di condivisione',
    shareLinkGenerated: 'Link di condivisione generato',
    copy: 'Copia',
    validFor: 'Valido per:',
    shareOptions: 'Opzioni di condivisione',
    qrCodeTitle: 'Codice QR E-Passport',
    qrContains: 'Questo QR contiene:',
    qrBullet1: 'Identificativo sicuro del passaporto',
    qrBullet2: 'Stato di verifica delle sezioni',
    qrBullet3: 'Accesso alle informazioni autorizzate',
    qrBullet4: 'Metadati di validita e condivisione',
    qrRawDocsPrivacyNote:
      'Nota: i documenti grezzi non vengono mai inclusi nei codici QR per privacy.',
    downloadQr: 'Scarica QR',
    detailedSectionBreakdown: 'Dettaglio sezione',
    records: 'Record',
    dataFields: 'Campi dati',
    addNewRecord: 'Aggiungi nuovo record',
    addNewDetails: 'Aggiungi nuovi dettagli',
    manageMicrochip: 'Gestisci microchip',
    linkMicrochip: 'Collega microchip',
    vaccinationRecordsShared:
      'I record vaccinali qui mostrati sono condivisibili nel passaporto.',
    noVaccinationRecords: 'Nessun record vaccinale',
    administered: 'Somministrato:',
    validUntil: 'Valido fino al',
    recorded: 'Registrato',
    medicalUpdatesShared:
      'Gli aggiornamenti medici qui mostrati sono condivisibili nel passaporto.',
    noMedicalUpdates: 'Nessun aggiornamento medico',
    selectDestinationDescription:
      'Scegli una destinazione per controllare i requisiti di ingresso.',
    selectDestinationCountryLabel: 'Paese di destinazione',
    addInRelevantSections: 'Aggiungi questi dati nelle sezioni pertinenti',
    microchipInfoShared:
      'Le informazioni microchip in questa sezione sono condivisibili nel passaporto.',
    statusCompleted: 'Completato',
    statusNeedsReview: 'Da revisionare',
    statusMissing: 'Mancante',
    review: 'Rivedi',
    addDetails: 'Aggiungi dettagli',
    physicalMetrics: 'Metriche fisiche',
    syncedWithHealthCarePhysical:
      'Sincronizzato con Salute e cura - sezione metriche fisiche.',
    noPhysicalMetricsYet: 'Nessuna metrica fisica registrata',
    weightLabel: 'Peso',
    heightLengthLabel: 'Altezza/Lunghezza',
    bodyConditionLabel: 'Condizione corporea',
    documentsSection: 'Sezione documenti',
    uploadDocument: 'Carica documento',
    noDocumentsUploadedYet: 'Nessun documento caricato',
    requiredDocument: 'Documento obbligatorio',
    download: 'Scarica',
    view: 'Visualizza',
    adminActions: 'Azioni amministrative',
    flagForReview: 'Segnala per revisione',
    requestUpdateFromOwner: 'Richiedi aggiornamento al proprietario',
    markSectionVerified: 'Segna sezione come verificata',
    documentsUploaded: '{{uploaded}}/{{total}} documenti caricati',
    fieldsCompleted: '{{completed}}/{{total}} campi completati',
    microchipLinkedSync: 'Microchip collegato e sincronizzato',
    failedToLinkMicrochip: 'Impossibile collegare il microchip. Riprova.',
    microchipUnlinked: 'Microchip scollegato',
    microchipUpdated: 'Microchip aggiornato',
  },
  editFieldModal: {
    add: 'Aggiungi',
    edit: 'Modifica',
    updateInfoBelow: 'Aggiorna le informazioni qui sotto.',
    enterField: 'Inserisci {{field}}',
    weightPlaceholder: 'es. 12.5 kg',
    weightHint:
      'Inserisci il peso in kg. Questo dato viene salvato su blockchain.',
    blockchainNote:
      'Le modifiche sono registrate sulla blockchain. I dati storici vengono preservati.',
    saving: 'Salvataggio...',
    saveChanges: 'Salva modifiche',
    updatedSuccess: '{{field}} aggiornato con successo.',
    saveFailed: 'Salvataggio non riuscito. Riprova.',
  },
  epassportModal: {
    uploadDocument: 'Carica documento',
    replaceDocument: 'Sostituisci documento',
    dropFileOrBrowse: 'Trascina qui un file oppure sfoglia',
    supportsPdfJpgPng: 'Supporta PDF, JPG, PNG (max 10 MB)',
    chooseFile: 'Scegli file',
    noteEncrypted:
      'I documenti sono archiviati in modo sicuro e cifrato. Solo tu e chi autorizzi potete accedervi.',
    uploading: 'Caricamento...',
    upload: 'Carica',
    replace: 'Sostituisci',
    uploadedOn: 'Caricato il',
    viewDocument: 'Visualizza documento',
    download: 'Scarica',
    uploadedByLabel: 'Caricato da',
    fileType: 'Tipo file',
    fileSize: 'Dimensione file',
    uploadedBy: 'Caricato da {{name}}',
    review: 'Rivedi',
    documentPreview: 'Anteprima documento',
    previewPlaceholder: 'Anteprima',
    print: 'Stampa',
    openInNewTab: 'Apri in una nuova scheda',
    close: 'Chiudi',
  },
  travelCheck: {
    region: {
      europe: 'Europa',
      northAmerica: 'Nord America',
      southAmerica: 'Sud America',
      asia: 'Asia',
      asiaPacific: 'Asia-Pacifico',
    },
    country: {
      UK: 'Regno Unito',
      FR: 'Francia',
      DE: 'Germania',
      IT: 'Italia',
      ES: 'Spagna',
      NL: 'Paesi Bassi',
      SE: 'Svezia',
      NO: 'Norvegia',
      CH: 'Svizzera',
      GR: 'Grecia',
      IE: 'Irlanda',
      US: 'Stati Uniti',
      CA: 'Canada',
      MX: 'Messico',
      BR: 'Brasile',
      AR: 'Argentina',
      CL: 'Cile',
      CO: 'Colombia',
      JP: 'Giappone',
      CN: 'Cina',
      SG: 'Singapore',
      TH: 'Thailandia',
      AU: 'Australia',
      NZ: 'Nuova Zelanda',
      IN: 'India',
      AE: 'Emirati Arabi Uniti',
      KR: 'Corea del Sud',
    },
    req: {
      rabiesVaccination: 'Vaccinazione antirabbica',
      healthCertificate10Days: 'Certificato sanitario (entro 10 giorni)',
      parasiteTreatment: 'Trattamento antiparassitario (24-120 ore)',
      microchipIso: 'Microchip ISO 11784/11785',
      euPetPassport: 'Passaporto europeo per animali',
      microchip: 'Microchip',
      healthCertificate: 'Certificato sanitario',
      tapewormTreatment: 'Trattamento contro tenia',
      importPermit: 'Permesso di importazione',
      cdcDogImportForm: 'Modulo CDC importazione cani',
      parasiteTreatmentCertificate: 'Certificato trattamento antiparassitario',
      rabiesVaccination2Doses: 'Vaccinazione antirabbica (2 dosi)',
      rabiesAntibodyTest: 'Test anticorpi rabbia',
      importNotification: 'Notifica di importazione',
      quarantine: 'Quarantena',
      importLicense: 'Licenza di importazione',
      rabiesFavnTest: 'Test FAVN rabbia',
      quarantineMin10Days: 'Quarantena (minimo 10 giorni)',
      rabiesVaccinationTiterTest: 'Vaccino rabbia e test titolo anticorpale',
      quarantine10_180Days: 'Quarantena (10-180 giorni)',
    },
    details: {
      completeSectionsToVerify: 'Completa le sezioni E-Passport per verificare',
      expiredRequiresRenewal: 'Scaduto - rinnovo richiesto',
      notCompleted: 'Non completato',
      requiredForEuTravel: 'Richiesto per viaggi UE',
      required: 'Richiesto',
      requiredWithin10Days: 'Richiesto entro 10 giorni',
      validForDogs: 'Valido per cani',
      mustCompleteOnline: 'Da completare online',
      validCertificateRequired: 'Certificato valido richiesto',
      recommended: 'Consigliato',
      requiredWithin5Days: 'Richiesto entro 5 giorni',
      hoursBeforeEntry: '24-120 ore prima dell ingresso',
      requiredForDogs: 'Richiesto per cani',
      applyInAdvance: 'Richiedere in anticipo',
      applyViaVigiagro: 'Richiedere tramite VIGIAGRO',
      governmentEndorsed: 'Convalidato dalle autorita',
      senasaPermitRequired: 'Permesso SENASA richiesto',
      sagApprovalRequired: 'Approvazione SAG richiesta',
      endorsedByOfficialVet: 'Convalidato da veterinario ufficiale',
      icaEndorsed: 'Convalidato ICA',
      validCertificate: 'Certificato valido',
      iso11784_11785: 'ISO 11784/11785',
      with30DayWait: 'Con attesa di 30 giorni',
      required180DaysBefore: 'Richiesto 180 giorni prima',
      daysBeforeArrival: '40 giorni prima dell arrivo',
      requiredFromAuthorities: 'Richiesto dalle autorita',
      officialEndorsementRequired: 'Convalida ufficiale richiesta',
      daysDependingOnCity: '7-30 giorni in base alla citta',
      applyToAvs: 'Richiedere ad AVS',
      ifNotFromApprovedCountry: 'Se non da paese approvato',
      fromLivestockDept: 'Dal Dipartimento Zootecnico',
      within10Days: 'Entro 10 giorni',
      fromDaff: 'Da DAFF',
      postArrivalQuarantine: 'Struttura quarantena post-arrivo',
      with180DayWait: 'Con attesa di 180 giorni',
      fromMpi: 'Da MPI',
      '180DayWaitingPeriod': 'Periodo di attesa di 180 giorni',
      dependsOnCountryOfOrigin: 'Dipende dal paese di origine',
      aqcsPermitRequired: 'Permesso AQCS richiesto',
      fromMinistryOfClimateChange: 'Dal Ministero del Cambiamento Climatico',
      isoRecommended: 'ISO consigliato',
      requiresVeterinaryEndorsement: 'Richiede convalida veterinaria',
    },
    notes: {
      waitingPeriod21Days:
        'Periodo di attesa di 21 giorni dopo vaccinazione antirabbica',
      petPassportOrAhc: 'Passaporto animale o AHC richiesto',
      tapewormForDogs: 'Trattamento tenia richiesto per cani',
      microchipBeforeVaccination:
        'Il microchip deve essere presente prima della vaccinazione',
      category1_2DogsPermit:
        'I cani di categoria 1 e 2 richiedono permessi speciali',
      dangerousBreedsList: 'Si applica l elenco razze pericolose',
      importPermitMayBeRequired:
        'Potrebbe essere richiesto un permesso di importazione',
      petPassportRequired: 'Passaporto animale richiesto',
      registerWithin7Days: 'Registrare entro 7 giorni dall arrivo',
      dangerousBreedsLicense:
        'Razze potenzialmente pericolose richiedono licenza speciale',
      tapewormTreatmentForDogs: 'Trattamento tenia per cani',
      highRiskBreedsProhibited: 'Razze ad alto rischio vietate',
      noTravelToRabiesAreas:
        'Dichiarazione di assenza viaggi in aree a rischio rabbia',
      max5PetsPerPerson: 'Massimo 5 animali per persona',
      importPermitYoungAnimals: 'Permesso importazione per animali giovani',
      strictRabiesRegulations: 'Norme antirabbiche molto severe',
      advanceApplicationRequired: 'Richiesta anticipata obbligatoria',
      specialRulesDangerousBreeds: 'Regole speciali per razze pericolose',
      veterinaryCertificateRequired: 'Certificato veterinario richiesto',
      leishmaniasisRecommended: 'Prevenzione leishmaniosi consigliata',
      healthCertWithin10Days: 'Certificato sanitario entro 10 giorni',
      stateSpecificRequirements:
        'Possono applicarsi requisiti specifici per stato',
      cdcRestrictions: 'Restrizioni CDC per paesi ad alto rischio',
      catsNoRabiesCert:
        'Per i gatti in genere non e richiesto certificato rabbia',
      youngPuppiesDifferentRules: 'I cuccioli giovani hanno regole diverse',
      healthyOnArrival: 'Deve essere sano all arrivo',
      max3MonthsStay:
        'Soggiorno massimo di 3 mesi senza permesso di importazione',
      authorizedPortsOnly: 'Ingresso solo da porti autorizzati',
      within60DaysOfHealthCert:
        'Arrivo entro 60 giorni dal certificato sanitario',
      airportQuarantineInspection:
        'Ispezione quarantena aeroportuale richiesta',
      arriveWithin10Days: 'Arrivo entro 10 giorni dal certificato',
      additionalVaccinationsMayBeRequired:
        'Possono essere richieste vaccinazioni aggiuntive',
      apply30DaysInAdvance: 'Richiedere il permesso 30 giorni prima',
      quarantineMayBeRequired: 'La quarantena puo essere richiesta',
      certificateValid10Days: 'Certificato valido per 10 giorni',
      inspectionAtAirport: 'Ispezione in aeroporto richiesta',
      veryStrictProcess: 'Procedura molto rigida - pianifica 7+ mesi prima',
      approvedLabsForTesting: 'Usare laboratori approvati per i test',
      '180DayWaitAfterBloodTest':
        'Periodo di attesa 180 giorni dopo esame sangue',
      breedRestrictionsApply: 'Si applicano restrizioni di razza',
      onePetPerPerson: 'Limite di un animale per persona',
      majorCitiesSpecificRules: 'Le grandi citta hanno regole specifiche',
      quarantine30DaysNonApproved:
        'Quarantena 30 giorni per paesi non approvati',
      applyLicense2WeeksBefore: 'Richiedere licenza 2 settimane prima',
      apply15DaysInAdvance: 'Richiedere permesso 15 giorni prima',
      approvedAirportsOnly: 'Ingresso solo da aeroporti approvati',
      extremelyStrict6Months: 'Estremamente rigido - pianifica 6+ mesi prima',
      costlyQuarantineRequired: 'Quarantena costosa richiesta',
      mustUseApprovedRoutes: 'Usare rotte approvate obbligatorio',
      apply2MonthsBefore: 'Richiedere permesso 2+ mesi prima',
      approvedCountriesShorterQuarantine:
        'I paesi approvati hanno quarantena piu breve',
      veryHighStandards: 'Standard molto elevati richiesti',
      apply60DaysInAdvance: 'Richiedere permesso 60 giorni prima',
      specificAirportsOnly: 'Ingresso consentito solo da aeroporti specifici',
      noQuarantineMostCountries:
        'Nessuna quarantena per la maggior parte dei paesi',
      originalHealthCertRequired: 'Certificato sanitario originale richiesto',
      quarantineInspectionAtAirport: 'Ispezione quarantena in aeroporto',
      bloodTestNonRabiesFree:
        'Puo essere richiesto test sangue da paesi non rabies-free',
    },
  },
  landing: {
    features: {
      zkp: {
        title: 'Verifiche privacy',
        subtitle: 'Crea e gestisci verifiche protette della privacy',
        verifyWithoutSharing: 'Verifica senza condividere',
        verifyWithoutSharingDesc:
          'Petso può confermare informazioni richieste senza esporre dettagli privati.',
        professionalsSeeVerified:
          'I professionisti vedono ✅ "Verificato", non i tuoi dati privati',
        proofsExpire: 'Le prove scadono automaticamente per sicurezza',
        youControl: 'Controlli tu cosa viene verificato',
        fullDataOption: "La condivisione dati completa è sempre un'opzione",
        activeVerifications: 'Verifiche attive',
        noVerificationsYet: 'Nessuna verifica ancora. Creane una per iniziare.',
        noVerificationsDesc:
          'Le verifiche privacy dimostrano condizioni senza esporre dati sensibili.',
        createVerification: 'Crea verifica',
        createFirstVerification: 'Crea la tua prima verifica',
        verificationDetails: 'Dettagli verifica',
        activeVerification: '✓ Verifica attiva',
        expiringSoon: '⚠️ In scadenza',
        expired: 'Scaduta',
        verificationId: 'ID verifica',
        verificationType: 'Tipo verifica',
        createdDate: 'Data creazione',
        expirationDate: 'Data scadenza',
        daysRemaining_one: '{{count}} giorno rimanente',
        daysRemaining_other: '{{count}} giorni rimanenti',
        usedFor: 'Usata per',
        usedForLabel: 'Usata per:',
        verificationStatus: 'Stato verifica',
        verified: '✓ Verificata',
        notVerified: '✗ Non verificata',
        privacyProtected: 'Privacy protetta',
        privacyProtectedDesc:
          'Questa verifica usa prove a conoscenza zero per confermare condizioni senza rivelare dati sensibili.',
        verificationTimeline: 'Timeline verifica',
        created: 'Creata',
        expires: 'Scade',
        backToList: 'Torna alla lista',
        reverifyNow: 'Verifica di nuovo ora',
        viewDetails: 'Vedi dettagli',
        revoke: 'Revoca',
        understandingVerificationStates: 'Comprendere gli stati di verifica',
        verifiedLabel: 'Verificato',
        verifiedDesc: 'Condizione soddisfatta (privacy protetta)',
        fullAccessLabel: 'Accesso completo',
        fullAccessDesc: 'Dati condivisi con consenso',
        hiddenLabel: 'Nascosto',
        hiddenDesc: 'Nessun accesso concesso',
        proofTypes: {
          vaccination: 'Stato vaccinazioni',
          'age-eligibility': 'Idoneità età',
          'weight-eligibility': 'Idoneità peso',
          'allergy-presence': 'Presenza allergie',
          'medication-status': 'Stato terapia',
          'license-valid': 'Licenza valida',
        },
        proofTypeDesc: {
          vaccination:
            'Conferma che l’animale è aggiornato senza condividere cartelle cliniche',
          'age-eligibility': "Conferma che l'età soddisfa i requisiti",
          'weight-eligibility': 'Conferma intervallo peso richiesto',
          'allergy-presence': 'Conferma presenza/assenza allergie richieste',
          'medication-status': 'Conferma stato terapia in sicurezza',
          'license-valid': 'Conferma validità licenza/certificazione',
        },
        proofTypeExample: {
          vaccination: 'Mostra "✅ Vaccinato" invece dei dettagli vaccinali',
          'age-eligibility':
            'Mostra "✅ Età idonea" senza esporre data di nascita',
          'weight-eligibility': 'Mostra "✅ Peso idoneo" senza mostrare i dati',
          'allergy-presence': 'Mostra solo esito allergie richiesto',
          'medication-status':
            'Mostra solo stato terapia richiesto per il servizio',
          'license-valid': 'Mostra licenza valida senza condividere dettagli',
        },
        setupFlow: {
          title: 'Configura verifica privacy',
          forContext: 'Per: {{context}}',
          contextBooking: 'Prenotazione',
          contextEmergency: 'Emergenza',
          contextProfessional: 'Verifica professionale',
          contextSharing: 'Condivisione',
          verifyWithoutSharingHeading:
            'Verifica senza condividere dati sensibili',
          verifyWithoutSharingIntro:
            'Puoi dimostrare che certe condizioni sono rispettate senza condividere i dati sottostanti.',
          howItWorks: 'Come funziona',
          step1Title: 'Scegli il tipo di verifica',
          step1Desc: 'Seleziona la prova da generare per il tuo caso.',
          step2Title: 'Generazione crittografica',
          step2Desc:
            'Creiamo una prova verificabile senza rivelare i record privati.',
          step3Title: 'Condividi solo l’esito',
          step3Desc: 'Il destinatario vede solo stato verificato e validità.',
          exampleLabel: 'Esempio:',
          whatProfessionalSees: 'Cosa vede il professionista',
          importantLabel: 'Importante:',
          noteExpire: 'Le verifiche hanno scadenza automatica',
          noteFullRecords: 'Puoi sempre scegliere la condivisione completa',
          noteEmergency: 'In emergenza puoi concedere più accesso',
          noteRevoke: 'Puoi revocare in qualsiasi momento',
          cancel: 'Annulla',
          continue: 'Continua',
          chooseVerificationType: 'Scegli tipo di verifica',
          selectWhatToVerify: 'Seleziona cosa verificare per {{context}}',
          privacyProtectedRecommended: 'Privacy protetta (consigliato)',
          shareBadgeOnly: 'Condividi solo badge/esito verificato',
          fullRecordsWarning:
            'Attenzione: stai scegliendo una modalità meno protetta.',
          back: 'Indietro',
          confirmVerification: 'Conferma verifica',
          reviewAndConfirm: 'Controlla i dettagli prima di procedere',
          verificationWillBeProtected: 'La verifica sarà privacy-protected',
          validFor6Months: 'Valida per 6 mesi',
          canBeRevokedAnytime: 'Revocabile in qualsiasi momento',
          consentLabel:
            'Confermo di voler creare questa verifica con i parametri selezionati.',
          verifyNow: 'Verifica ora',
          creatingVerification: 'Creazione verifica in corso',
          encryptingAndGenerating: 'Cifratura e generazione della prova...',
          verificationCreated: 'Verifica creata',
          verificationActive: 'La verifica è ora attiva e utilizzabile',
          validUntil: 'Valida fino al',
        },
      },
    },
  },
  petOverview: {
    title: 'Profilo animale',
    privacyVerification: 'Privacy e verifica',
    bookForThisPet: 'Prenota per questo animale',
    comingSoon: 'Prossimamente',
    shareAccess: 'Condividi / Accesso',
    healthRecords: 'Cartelle cliniche',
    aiConsent: 'Consenso IA',
    aiInsights: 'Insight IA',
    editPetProfile: 'Modifica profilo animale',
    editProfile: 'Modifica profilo',
    mintFunNft: 'Crea NFT divertente',
    viewNftGallery: 'Vedi galleria NFT',
    nftJustForFunNotice:
      'Solo per divertimento 🎉 Nessun record o identità collegata',
    basicInformation: 'Informazioni di base',
    species: 'Specie',
    breed: 'Razza',
    birthdate: 'Data di nascita',
    yearsOld_one: '{{count}} anno',
    yearsOld_other: '{{count}} anni',
    gender: 'Genere',
    color: 'Colore',
    petStatus: "Stato dell'animale",
    alive: 'Vivo',
    deceased: 'Deceduto',
    deceasedDescription:
      'Contrassegnare un animale come deceduto conserva i record e sospende le attività attive.',
    deceasedInfoTitle: '{{name}} è stato contrassegnato come deceduto',
    deceasedInfoBody:
      'Tutti i record vengono conservati. Appuntamenti e servizi attivi sono stati sospesi. Puoi comunque accedere a cartelle cliniche, DID e funzioni commemorative.',
    lostPetStatus: 'Stato animale smarrito',
    reportPetAsLost: 'Segnala animale smarrito',
    reportLostDescription:
      'Segnalando come smarrito, il tuo animale sarà ricercabile nel sistema Global Pet Recovery.',
    cannotReportDeceased:
      'Non puoi segnalare come smarrito un animale deceduto.',
    markAsFound: 'Segna come ritrovato',
    petIsFound: 'Animale ritrovato - Segna come non smarrito',
    isReportedAsLost: '{{name}} è segnalato come smarrito',
    lostBannerBody:
      'Questo animale è visibile nella ricerca Global Pet Recovery. Chiunque può cercare {{name}} con DID o numero microchip.',
    lostStatusCurrentlyLost: '{{name}} risulta attualmente smarrito',
    lostStatusContactYou:
      'Il tuo animale è visibile nella ricerca globale. Chi trova {{name}} può contattarti tramite Petso.',
    locationAmendable: 'Posizione (modificabile)',
    country: 'Paese',
    city: 'Città',
    notSpecified: 'Non specificato',
    selectCountry: 'Seleziona paese',
    cityOptional: 'Città (opzionale)',
    selectCityOptional: 'Seleziona città (opzionale)',
    selectCountryFirst: 'Seleziona prima un paese',
    other: 'Altro',
    enterCityName: 'Inserisci nome città',
    enterYourCity: 'Inserisci la tua città',
    identification: 'Identificazione',
    microchipId: 'ID microchip',
    owner: 'Proprietario',
    petDid: 'DID animale',
    na: 'N/D',
    notCreated: 'Non creato',
    mother: 'Madre',
    father: 'Padre',
    linkedNotInYourPets: 'Collegato (non nelle tue mascotte)',
    primaryOwner: 'Proprietario principale',
    addOwnerOrCaretaker: 'Aggiungi proprietario o caregiver',
    emailAddressRequired: 'Email (obbligatoria)',
    emailPlaceholder: 'es. amico@esempio.com',
    roleLabel: 'Ruolo',
    noteOptional: 'Nota (opzionale)',
    notePlaceholderInvite: 'Messaggio opzionale per invito di {{name}}',
    sendingInvitation: 'Invio in corso...',
    sendInvitation: 'Invia invito',
    didCardTitle: 'Identità decentralizzata (DID)',
    active: 'Attivo',
    verifiedOperational: 'Verificato e operativo',
    didRevoked: 'DID revocato',
    revokeDid: 'Revoca DID',
    restoreDid: 'Ripristina DID',
    transferOwnership: 'Trasferisci proprietà',
    transferOwnershipComingSoon:
      'Trasferimento proprietà in arrivo! Potrai trasferire il DID a un altro proprietario.',
    copyDid: 'Copia DID',
    viewOnSolana: 'Visualizza su Solana',
    manageDid: 'Gestisci DID',
    didDescription:
      "Questo identificatore univoco e immutabile è protetto su blockchain e può essere usato per verificare identità e record dell'animale.",
    processing: 'Elaborazione...',
    revokeDidTitle: 'Revoca DID',
    revokeConfirmRequired: 'Questa azione richiede conferma',
    revokeConfirmMessage: 'Sei sicuro di voler revocare il DID di {{name}}?',
    revokeWhatHappens: 'Cosa succede revocando:',
    revokeBullet1: "L'identità sarà contrassegnata come inattiva",
    revokeBullet2: 'Le richieste di verifica falliranno',
    revokeBullet3: 'Potrai ripristinarla in seguito se necessario',
    confirmRevoke: 'Conferma revoca',
    greatNews: 'Ottime notizie!',
    markFoundConfirmMessage: 'Conferma che {{name}} è stato ritrovato',
    markFoundConfirmBody:
      'Segnando {{name}} come ritrovato verrà rimosso dalla ricerca globale. Siamo felici che sia al sicuro!',
    confirmFound: 'Conferma ritrovamento',
    failedToRestoreDid: 'Impossibile ripristinare DID. Riprova.',
    failedToRevokeDid: 'Impossibile revocare DID. Riprova.',
    failedToMarkFound: "Impossibile segnare l'animale come ritrovato. Riprova.",
  },
  nft: {
    gallery: {
      title: 'Galleria NFT',
      subtitle: 'Collezionabili divertenti collegati a questo animale',
      rememberNotice:
        'Questi NFT sono solo per divertimento. Non rappresentano proprietà o identità ufficiale.',
      tokenId: 'Token ID',
      funOnly: 'Solo divertimento',
      minted: 'Creato il {{date}}',
      noNftsYet: 'Nessun NFT ancora',
      mintFirstPrompt: 'Crea il tuo primo NFT divertente per questo animale.',
      mintFunNft: 'Crea NFT divertente',
      detailsTitle: 'Dettagli NFT',
      funOnlyNoUtility: 'Solo collezionabile — nessuna utilità o identità',
      petName: "Nome dell'animale",
      caption: 'Descrizione',
      network: 'Rete',
      mintDate: 'Data creazione',
      noticeNotLinked:
        "Questo NFT non è collegato all'identità ufficiale o ai record sanitari.",
      viewOnSolana: 'Visualizza su Solana',
      downloadImage: 'Scarica immagine',
      transferNft: 'Trasferisci NFT',
      hideFromGallery: 'Nascondi dalla galleria',
      burnNft: 'Brucia NFT',
      burnConfirm: 'Bruciare questo NFT definitivamente? Azione irreversibile.',
    },
    mint: {
      title: 'Crea NFT divertente',
      subtitle: 'Crea un collezionabile per questo animale',
      beforeYouMint: 'Prima di creare',
      readImportantInfo: 'Leggi queste informazioni importanti.',
      funCollectibleOnly: 'Questo è solo un collezionabile divertente',
      doesNotRepresent: 'Non rappresenta proprietà o identità ufficiale',
      noConnection: 'Nessun collegamento a cartelle cliniche o microchip',
      noUtility: 'Nessuna utilità o accesso speciale',
      sharableCollectible: 'Collezionabile condivisibile da mostrare/scambiare',
      whatThisMeans: 'Cosa significa',
      canShareSocial: 'Puoi condividerlo sui social',
      canTransferSell: 'Puoi trasferirlo o venderlo',
      yoursToCollect: 'È tuo da collezionare e mostrare',
      notProveOwnership: 'Non prova la proprietà dell’animale',
      notHealthRecords: 'Non contiene cartelle cliniche',
      notSpecialAccess: 'Non concede accessi speciali',
      iUnderstand:
        "Comprendo che questo NFT è solo per divertimento e non è collegato all'identità o ai record del mio animale.",
      continueToCreate: 'Continua a creare',
      chooseArtwork: 'Scegli artwork',
      selectHowLook: 'Scegli come apparirà il tuo NFT',
      useProfilePhoto: 'Usa foto profilo',
      currentPhoto: 'Foto attuale animale',
      uploadNewPhoto: 'Carica nuova foto',
      chooseDifferentImage: 'Scegli un’immagine diversa',
      choosePhoto: 'Scegli foto',
      changePhoto: 'Cambia foto',
      pleaseChoosePhoto: 'Scegli una foto per continuare.',
      orChooseTemplate: 'Oppure scegli un template',
      cartoon: 'Cartoon',
      pixelArt: 'Pixel art',
      sticker: 'Sticker',
      seasonal: 'Stagionale',
      livePreviewNextStep: 'Anteprima live nel prossimo step',
      customizeTitle: 'Personalizza il tuo NFT',
      purelyCosmetic: "Solo estetico — nessun impatto sull'identità",
      cosmeticOnly: 'Solo estetico',
      livePreview: 'Anteprima live',
      nicknameOptional: 'Soprannome (opzionale)',
      notOfficialName: 'Non è il nome ufficiale dell’animale',
      funCaptionOptional: 'Caption divertente (opzionale)',
      captionPlaceholder: 'es. Miglior amico',
      frameStyle: 'Stile cornice',
      frameNone: 'Nessuna',
      frameGoldBorder: 'Cornice oro',
      frameShadow: 'Ombra',
      frameNeon: 'Neon',
      background: 'Sfondo',
      bgBluePurple: 'Blu / Viola',
      bgPinkOrange: 'Rosa / Arancione',
      bgGreenTeal: 'Verde / Turchese',
      bgGray: 'Grigio',
      chooseSeasonalTheme: 'Scegli tema stagionale',
      seasonChristmas: 'Natale',
      seasonHalloween: 'Halloween',
      seasonEaster: 'Pasqua',
      seasonWinter: 'Inverno',
      seasonSummer: 'Estate',
      seasonRain: 'Pioggia',
      readyToMint: 'Pronto a creare',
      reviewDetails: 'Controlla i dettagli qui sotto.',
      type: 'Tipo',
      typeCollectible: 'Collezionabile',
      utility: 'Utilità',
      utilityNone: 'Nessuna',
      royalties: 'Royalties',
      royaltiesNone: 'Nessuna',
      transferable: 'Trasferibile',
      transferableYes: 'Sì',
      collection: 'Collezione',
      collectionName: 'Petso Fun NFTs',
      reminderCosmetic:
        "È un collezionabile divertente. Non è collegato all'identità o ai record dell'animale.",
      mintingTitle: 'Creazione NFT in corso',
      mintingSubtitle: 'Potrebbe richiedere qualche istante...',
      preparingArtwork: 'Preparazione artwork',
      uploadingIpfs: 'Caricamento su IPFS',
      mintingSolana: 'Mint su Solana',
      successTitle: 'Il tuo NFT è pronto!',
      successSubtitle: 'Il tuo collezionabile è stato creato.',
      funOnlyBadge: 'Solo divertimento — nessuna utilità',
      viewNft: 'Vedi NFT',
      share: 'Condividi',
      mintAnother: 'Crea un altro',
      backToProfile: 'Torna al profilo',
      back: 'Indietro',
      next: 'Avanti',
      mintForFun: 'Crea per divertimento',
      network: 'Rete',
    },
  },
  family: {
    viewInFamilyTree: 'Visualizza nell’albero genealogico →',
    title: 'Albero genealogico',
    subtitle:
      'Mappa le relazioni familiari del tuo animale con collegamenti verificabili.',
    privacyLevel: 'Livello privacy',
    privateOnlyMe: 'Privato (solo io)',
    familyPetFamily: 'Famiglia (famiglia animale)',
    public: 'Pubblico',
    didBasedAncestryLinking: 'Collegamento genealogia basato su DID',
    buildTreeDescription:
      'Costruisci una genealogia affidabile collegando parenti, fratelli e cucciolate.',
    currentPet: 'Animale corrente',
    currentPetAndSiblings: 'Animale corrente e fratelli',
    parents: 'Genitori',
    grandparents: 'Nonni',
    addSibling: 'Aggiungi fratello/sorella',
    addMother: 'Aggiungi madre',
    addFather: 'Aggiungi padre',
    addMaternalGrandmother: 'Aggiungi nonna materna',
    addMaternalGrandfather: 'Aggiungi nonno materno',
    addPaternalGrandmother: 'Aggiungi nonna paterna',
    addPaternalGrandfather: 'Aggiungi nonno paterno',
    dnaHeritage: 'DNA e eredita',
    familyMembersCount: 'Membri familiari',
    verifiedCount: 'verificati',
    siblingsCount: 'Fratelli/sorelle',
    sameLineage: 'Stessa linea genealogica',
    ancestryDidsCount: 'Ancestry DID',
    placeholderIdentities: 'Identita segnaposto',
    treeStatusLabel: 'Stato albero',
    treeStatusValueEmpty: 'Vuoto',
    treeStatusValuePartial: 'Parziale',
    treeStatusValueComplete: 'Completo',
    treeStatusEmpty: 'Inizia aggiungendo i primi familiari',
    treeStatusPartial: 'Albero in crescita',
    treeStatusComplete: 'Albero genealogico completo',
    unknownPet: 'Animale sconosciuto',
    verifiedBadge: 'Verificato',
    ancestryDidBadge: 'Ancestry DID',
    ownerAddedBadge: 'Aggiunto dal proprietario',
    verifiedDid: 'DID verificato',
    ancestryDid: 'Ancestry DID',
    blockchainVerified: 'Verificato su blockchain',
    statusDeceased: 'In memoria',
    statusMissing: 'Smarrito',
    addMemberModal: {
      adding: 'Aggiunta: {{relationship}}',
    },
    relationships: {
      mother: 'Madre',
      father: 'Padre',
      sibling: 'Fratello/Sorella',
      currentPet: 'Animale corrente',
      offspring: 'Cucciolata',
      maternalGrandmother: 'Nonna materna',
      maternalGrandfather: 'Nonno materno',
      paternalGrandmother: 'Nonna paterna',
      paternalGrandfather: 'Nonno paterno',
    },
    relationshipLabel: {
      Sibling: 'Fratello/Sorella',
      Mother: 'Madre',
      Father: 'Padre',
      'Current Pet': 'Animale corrente',
      Offspring: 'Cucciolata',
      'Maternal Grandmother': 'Nonna materna',
      'Maternal Grandfather': 'Nonno materno',
      'Paternal Grandmother': 'Nonna paterna',
      'Paternal Grandfather': 'Nonno paterno',
      'Grandmother (Maternal)': 'Nonna materna',
      'Grandfather (Maternal)': 'Nonno materno',
      'Grandmother (Paternal)': 'Nonna paterna',
      'Grandfather (Paternal)': 'Nonno paterno',
    },
    modals: {
      addFamilyMemberTitle: 'Aggiungi membro famiglia',
      adding: 'Aggiunta: {{relationship}}',
      chooseHowToAdd: 'Scegli come aggiungere questo membro.',
      searchPetsoDatabase: 'Cerca nel database Petso',
      findExistingPets: 'Trova animali gia registrati e collegali subito.',
      recommendedAutoVerified: 'Consigliato: verifica automatica',
      addManually: 'Aggiungi manualmente',
      enterDetailsManually: 'Inserisci i dettagli manualmente.',
      ownerAddedUnverified: 'Aggiunto dal proprietario (non verificato)',
      createAncestryDid: 'Crea Ancestry DID',
      createPlaceholderIdentity: 'Crea una identita segnaposto collegabile.',
      futureProofCanBeLinked: 'Future-proof: collegabile in seguito',
      buildingAncestry: 'Costruisci la genealogia gradualmente',
      familyTreesOptional:
        'Gli alberi genealogici sono opzionali e modificabili in qualsiasi momento.',
      addingToFamilyTree: 'Aggiunta all albero genealogico',
      createAncestryDidTitle: 'Crea Ancestry DID',
      createAncestryDidRecommended: 'Consigliato per continuita genealogica',
      creatingAncestryDidFor: 'Creazione Ancestry DID per {{name}}',
      ancestryDidDescription:
        'Una identita genealogica verificabile utile quando il profilo completo non e disponibile.',
      optionalRecommended: 'Opzionale ma consigliato',
      createsBlockchainRecord: 'Crea un record su blockchain',
      futureProofLinking: 'Consente collegamenti futuri',
      noOwnerRequired: 'Nessun proprietario richiesto',
      noOwnerRequiredDesc:
        'Puoi creare il collegamento senza account proprietario.',
      canBeLinkedWhenOwnerJoins:
        'Puo essere collegato al profilo reale quando il proprietario si registra.',
      createLaterFromProfile: 'Puoi crearlo in seguito dal profilo.',
      skipForNow: 'Salta per ora',
      confidenceLevels: 'Livelli di confidenza',
      confidenceLevelsHelp: 'Seleziona quanto sei certo della relazione.',
      confidenceHelp: 'Questo aiuta a qualificare la qualita del dato.',
      confirmRelationshipTitle: 'Conferma relazione',
      confirmAddToTree: 'Conferma aggiunta all albero',
      relationshipLabel: 'Relazione',
      howConfident: 'Quanto sei sicuro?',
      certain: 'Certo',
      certainDesc: 'Relazione confermata',
      estimated: 'Stimata',
      estimatedDesc: 'Relazione probabile',
      unknown: 'Sconosciuto',
      unknownDesc: 'Relazione non confermata',
      ownerAdded: 'Aggiunto dal proprietario',
      finalStep: 'Passaggio finale',
      whatIsAncestryDid: 'Cos e un Ancestry DID?',
      generatesUniqueDid: 'Genera un DID genealogico univoco',
      ensuresTreeComplete: 'Aiuta a mantenere completo l albero',
      maintainsLineageContinuity: 'Mantiene continuita della linea genealogica',
    },
    searchModal: {
      title: 'Cerca membro famiglia',
      petNameOrBreed: 'Nome animale o razza',
      petNameOrBreedPlaceholder: 'Es. Luna, Labrador...',
      microchipIdOptional: 'ID microchip (opzionale)',
      breederRefOptional: 'Riferimento allevatore (opzionale)',
      breederRefPlaceholder: 'Es. ALV-2026-001',
      searchButton: 'Cerca',
      searching: 'Ricerca in corso...',
      searchResults: 'Risultati ricerca',
      found: '{{count}} trovati',
      selectAs: 'Seleziona come {{relationship}}',
      noMatchesFound: 'Nessuna corrispondenza trovata',
      noMatchesDesc: 'Nessun animale corrisponde ai criteri inseriti.',
      tryAddingManually: 'Prova ad aggiungere manualmente',
      back: 'Indietro',
      errorCriteria: 'Inserisci almeno un criterio di ricerca.',
      errorFailed: 'Ricerca non riuscita. Riprova.',
      searchTips: 'Suggerimenti ricerca',
      tip1: 'Prova con nome completo o parziale',
      tip2: 'Usa ID microchip se disponibile',
      tip3: 'Controlla eventuali errori di digitazione',
      tip4: 'Prova a usare la razza come filtro',
      gender: 'Genere',
      ownerId: 'ID proprietario',
      microchip: 'Microchip',
    },
    manualModal: {
      title: 'Aggiungi manualmente',
      ownerAddedEntry: 'Voce aggiunta dal proprietario',
      ownerAddedDesc:
        'Questa voce non e verificata finche non viene collegata a un DID.',
      basicDetailsRequired: 'Dettagli base richiesti',
      petName: 'Nome animale',
      petNamePlaceholder: 'Inserisci nome',
      species: 'Specie',
      speciesDog: 'Cane',
      speciesCat: 'Gatto',
      speciesOther: 'Altro',
      selectSpecies: 'Seleziona specie',
      gender: 'Genere',
      selectGenderOptional: 'Seleziona genere (opzionale)',
      male: 'Maschio',
      female: 'Femmina',
      genderInferred: 'Il genere puo essere aggiornato in seguito.',
      breed: 'Razza',
      selectBreedOptional: 'Seleziona razza (opzionale)',
      optionalDetails: 'Dettagli opzionali',
      estimatedBirthYear: 'Anno nascita stimato',
      selectYearOptional: 'Seleziona anno (opzionale)',
      colorMarkings: 'Colore / segni distintivi',
      colorPlaceholder: 'Es. nero focato, bianco e marrone',
      breederName: 'Nome allevatore',
      breederNamePlaceholder: 'Es. Allevamento Rossi',
      notesHistory: 'Note / storia',
      notesPlaceholder: 'Aggiungi note utili su storia o parentela',
      afterAdding: 'Dopo aggiunta',
      afterAddingDesc:
        'Potrai collegare questa voce a un DID o modificarla in seguito.',
      back: 'Indietro',
      continue: 'Continua',
    },
    recordBirth: 'Registra nascita / cucciolata',
    recordBirthTooltip:
      'Registra nascita / cucciolata e collega i cuccioli all albero genealogico.',
    birth: {
      recordBirth: 'Registra nascita / cucciolata',
      puppyDetails: 'Dettagli cuccioli',
      step1Subtitle: 'Evento di nascita',
      step2Subtitle: 'Conferma cuccioli',
      dateOfBirth: 'Data di nascita',
      dateRequired: 'La data di nascita e obbligatoria.',
      dateNotFuture: 'La data di nascita non puo essere nel futuro.',
      numberOfPuppies: 'Numero di cuccioli',
      litterSizeInvalid: 'Inserisci un valore tra 1 e 20.',
      thisPetIs: 'Questo animale e',
      otherParent: 'Altro genitore (opzionale)',
      searchByPetDid: 'Cerca per Pet DID',
      noPetForDid: 'Nessun animale trovato per questo DID.',
      location: 'Localita',
      locationPlaceholder: 'Es. Milano, Italia',
      notes: 'Note',
      editPuppyDetails:
        'Modifica i dettagli per ogni cucciolo. Un Pet DID univoco verra assegnato alla creazione.',
      puppyNumber: 'Cucciolo',
      name: 'Nome',
      gender: 'Genere',
      male: 'Maschio',
      female: 'Femmina',
      colorMarkings: 'Colore / segni distintivi',
      pendingOwnershipTransfer: 'Trasferimento proprieta in attesa',
      confirmBirthCreatePuppies: 'Conferma nascita e crea cuccioli',
      celebrate: 'Nascita registrata!',
      puppiesCreated: 'I cuccioli sono stati aggiunti all albero genealogico.',
      success: 'Nascita registrata e cuccioli creati.',
      error: 'Registrazione nascita non riuscita. Riprova.',
      bornOn: 'Nato il {{date}}',
      litterBornOn: 'Cucciolata nata il {{date}}',
      puppiesRegistered: '{{count}} cuccioli registrati',
      parents: 'Genitori',
      ownership: 'Proprietà',
      mother: 'Madre',
      father: 'Padre',
      currentOwner: 'Proprietario attuale',
      transferStatus: 'Stato trasferimento',
      pendingTransfer: 'Trasferimento in attesa',
      transferOwnership: 'Trasferisci proprietà',
      growthMilestones: 'Tappe di crescita',
      milestoneBorn: 'Nascita',
      milestoneMicrochipped: 'Microchip',
      milestoneVaccinated: 'Vaccinato',
      milestoneTransferred: 'Trasferito',
      active: 'Attivo',
    },
  },
  documents: {
    title: 'Documenti',
    upload: 'Carica documento',
    uploadNew: 'Carica nuovo documento',
    uploadNewHint: 'Aggiungi certificati, referti e file utili',
    gridView: 'Vista griglia',
    listView: 'Vista elenco',
    filterByType: 'Filtra per tipo',
    filterAll: 'Tutti i tipi',
    noDocumentsForType: 'Nessun documento per questo tipo',
    blockchainSecuredTitle: 'Archiviazione documenti protetta',
    blockchainSecuredDesc:
      'I documenti sono gestiti in modo sicuro e tracciabile.',
    errorLoadCredentials:
      'Impossibile caricare i documenti al momento. Riprova tra poco.',
    preview: 'Anteprima',
    openInNewTab: 'Apri in nuova scheda',
    previewNotSupported: 'Anteprima non supportata per questo file',
    removeConfirm: 'Confermi la rimozione di questo documento?',
    documentHash: 'Hash documento',
    documentName: 'Nome documento',
    type: 'Tipo',
    actions: 'Azioni',
    supportedTypes: 'Tipi documento supportati',
    typeCardVaccinationTitle: 'Vaccinazioni',
    typeCardVaccinationDesc: 'Certificati e storico vaccinale',
    typeCardHealthTitle: 'Salute',
    typeCardHealthDesc: 'Certificati sanitari e referti',
    typeCardDnaTitle: 'DNA e laboratorio',
    typeCardDnaDesc: 'Report DNA e risultati test',
    typeCardOwnershipTitle: 'Proprieta',
    typeCardOwnershipDesc: 'Documenti di proprieta e pedigree',
    addChoiceModal: {
      title: 'Aggiungi documento',
      subtitle: 'Scegli come vuoi aggiungere il documento',
      uploadFile: 'Carica file',
      uploadFileDesc: 'Carica PDF o immagini dal dispositivo',
      scanTakePhoto: 'Scansiona / Foto',
      scanTakePhotoDesc: 'Acquisisci il documento con fotocamera',
      cancel: 'Annulla',
    },
    loadingDocuments: 'Caricamento documenti...',
    noDocuments: 'Nessun documento caricato',
    noDocumentsGetStarted:
      'Carica certificati vaccinali, certificati sanitari e altri documenti per iniziare.',
    documentTimelineTitle: 'Timeline documenti',
    documentTimelineSubtitle: 'Tutti i documenti con stato verifica',
    filterByCategory: 'Filtra per categoria',
    allCategories: 'Tutte le categorie',
    filterByVerification: 'Filtra per verifica',
    allDocuments: 'Tutti i documenti',
    verifiedOnly: 'Solo verificati',
    unverifiedOnly: 'Solo non verificati',
    totalDocuments: 'Documenti totali',
    verified: 'Verificati',
    ownerUploaded: 'Caricati dal proprietario',
    uploadedBy: 'Caricato da',
    noDocumentsMatching: 'Nessun documento trovato con questi filtri',
    download: 'Scarica',
    unnamedDocument: 'Documento',
    removeDocument: 'Documento rimosso',
    documentUploaded: 'Documento caricato',
    types: {
      vaccination: 'Record vaccinazione',
      healthCertificate: 'Certificato sanitario',
      dnaReport: 'Report DNA',
      ownershipRecord: 'Record di proprietà',
      microchipCertificate: 'Certificato microchip',
      pedigreeCertificate: 'Certificato pedigree',
      medical: 'Cartella clinica',
      insurance: 'Documento assicurazione',
      registration: 'Registrazione',
      passport: 'Passaporto',
      labResult: 'Referto laboratorio',
      xrayScan: 'Radiografia/Scan',
      surgicalReport: 'Referto chirurgico',
      dentalRecord: 'Record dentale',
      other: 'Altro',
    },
    uploadModal: {
      title: 'Carica documento',
      subtitle: 'Aggiungi un nuovo documento ai record del tuo animale',
      dragDropOrBrowse: 'Trascina qui i file oppure',
      browse: 'Sfoglia file',
      supportsPdfJpgPng: 'Supporta PDF, JPG, PNG, DOC, DOCX',
      documentType: 'Tipo documento',
      selectDocumentType: 'Seleziona tipo documento',
      documentName: 'Nome documento',
      documentNamePlaceholder: 'Inserisci nome documento',
      documentDate: 'Data',
      notesOptional: 'Note (opzionale)',
      notesOptionalPlaceholder: 'Aggiungi eventuali note...',
      blockchainStorage: 'Archiviazione blockchain',
      blockchainStorageDesc:
        "Questo documento sarà salvato su blockchain e collegato al DID dell'animale.",
      removeFile: 'Rimuovi',
      cancel: 'Annulla',
      uploadDocument: 'Carica documento',
      uploading: 'Caricamento...',
    },
    badgeOnChain: 'Certificato on-chain',
    badgeProfessionalVerified: 'Verificato da professionista',
    badgeOwnerUploaded: 'Caricato dal proprietario',
    verificationBadgesTitle: 'Significato badge di verifica',
    onChainTitle: 'Certificato on-chain:',
    onChainDesc:
      'Hash del documento salvato su blockchain. Verificabile in modo indipendente.',
    professionalVerifiedTitle: 'Verificato da professionista:',
    professionalVerifiedDesc:
      'Caricato da professionista verificato (veterinario, toelettatore, ecc.).',
    ownerUploadedTitle: 'Caricato dal proprietario:',
    ownerUploadedDesc:
      'Caricato dal proprietario. Può essere verificato successivamente da un professionista.',
    typeVaccinationCertificate: 'Certificato vaccinale',
    typeHealthCertificate: 'Certificato sanitario',
    typeLabResults: 'Referti di laboratorio',
    typeXrayImaging: 'Radiografia / Imaging',
    typeMedicalRecord: 'Cartella clinica',
    typeInsurance: 'Assicurazione',
    typeGroomingReport: 'Report toelettatura',
    typeOther: 'Altro',
    downloadStarted: 'Download avviato',
    downloadFailed: 'Download documento non riuscito',
  },
  common: {
    continue: 'Continua',
    cancel: 'Annulla',
    close: 'Chiudi',
    back: 'Indietro',
    next: 'Avanti',
    search: 'Cerca',
    loading: 'Caricamento...',
    saving: 'Salvataggio...',
    delete: 'Elimina',
    error: 'Errore',
    pet: 'Animale',
    owner: 'Proprietario',
    optional: 'opzionale',
    done: 'Fatto',
    copy: 'Copia',
    copied: 'Copiato',
    print: 'Stampa',
    download: 'Scarica',
  },
  settings: {
    title: 'Impostazioni profilo proprietario',
    subtitle: 'Gestisci le tue informazioni personali e i dettagli di contatto',
    tabs: {
      profile: 'Profilo',
      wallet: 'Abbonamento e Wallet',
      security: 'Sicurezza',
    },
    profile: {
      saveChanges: 'Salva modifiche',
      onboardingRequired:
        'Completa i campi obbligatori per continuare con la configurazione.',
      fixErrors: 'Correggi i seguenti errori',
      fullName: 'Nome completo',
      emailAddress: 'Indirizzo email',
      contactNumber: 'Numero di contatto',
      whatsapp: 'Numero WhatsApp',
      ownerTypeLabel: 'Io sono',
      walletAddress: 'Indirizzo wallet',
      managedInWallet: 'Gestito in impostazioni Wallet',
      address: 'Indirizzo',
      streetAddress: 'Indirizzo (via e numero)',
      country: 'Paese',
      state: 'Provincia/Regione',
      city: 'Città',
      postalCode: 'CAP',
      socialMedia: 'Social media',
      socialMediaDesc:
        'Aggiungi i tuoi profili social per facilitare connessioni e fiducia.',
      personalProfile: 'Profilo personale',
      professionalProfile: 'Profilo professionale',
      socialProfile: 'Profilo social',
      ownerPhoto: 'Foto del proprietario',
      ownerProfilePhotoAlt: 'Profilo del proprietario',
      uploadImage: 'Carica immagine',
      validation: {
        nameRequired: 'Il nome è obbligatorio',
        emailRequired: 'L’email è obbligatoria',
        contactRequired: 'Il numero di contatto è obbligatorio',
        completeMandatoryFields: 'Compila tutti i campi obbligatori',
      },
      image: {
        signInAgain: 'Accedi di nuovo per aggiornare la foto del profilo.',
        updated: 'Foto profilo aggiornata.',
        uploadFailed: 'Caricamento della foto profilo non riuscito.',
        googleClerkDefault:
          'La foto Google/Clerk viene usata come predefinita.',
        photoAdded: 'Foto aggiunta. Usata nei post social.',
        noPhoto: 'Nessuna foto aggiunta.',
      },
    },
    security: {
      title: 'Sicurezza',
      manageAuthDesc:
        'Gestisci autenticazione, password e impostazioni di sicurezza',
      twoFactor: 'Autenticazione a due fattori',
      twoFactorDesc: 'Aggiungi un livello extra di sicurezza al tuo account',
      status: 'Stato',
      enabled: 'Attiva',
      disabled: 'Disattivata',
      reconfigure2FA: 'Riconfigura 2FA',
      viewRecoveryCodes: 'Visualizza codici di recupero',
      enable2FAButton: 'Attiva 2FA',
      password: 'Password',
      passwordDesc:
        'Aggiorna regolarmente la password per mantenere l’account sicuro',
      lastChanged: 'Ultima modifica:',
      resetPassword: 'Reimposta password',
      deleteAccount: 'Elimina account',
      deleteAccountDesc:
        'Questa azione è irreversibile. Tutti i dati verranno eliminati definitivamente.',
      deleteAccountConfirm: 'Sei sicuro di voler eliminare il tuo account?',
      accountDeletionInitiated: 'Procedura di eliminazione account avviata.',
      securityBestPractices: 'Buone pratiche di sicurezza',
      tip1: 'Usa una password lunga e unica',
      tip2: 'Attiva l’autenticazione a due fattori (2FA)',
      tip3: 'Non condividere codici o password',
      tip4: 'Controlla periodicamente le attività del tuo account',
      twoFAEnabledSuccess: '2FA attivata con successo.',
      recoveryCodes: 'Codici di recupero',
      modal: {
        setup2faTitle: 'Configura l’autenticazione a due fattori',
        scanQrDescription:
          'Scansiona questo codice QR con la tua app di autenticazione (Google Authenticator, Authy, ecc.)',
        manualKeyPrompt: 'Oppure inserisci questa chiave manualmente:',
        secretKeyCopied: 'Chiave segreta copiata negli appunti!',
        enterVerificationCode: 'Inserisci il codice di verifica dalla tua app',
        verify: 'Verifica',
        twoFaEnabledTitle: '2FA attivata con successo!',
        saveRecoveryCodesDescription:
          'Salva questi codici di recupero in un posto sicuro. Ti serviranno se perdi l’accesso all’autenticatore.',
        recoveryCodesCopied: 'Codici di recupero copiati!',
        copyAll: 'Copia tutto',
        passwordsDoNotMatch: 'Le password non corrispondono!',
        passwordResetSuccess: 'Password reimpostata con successo!',
        currentPassword: 'Password attuale',
        newPassword: 'Nuova password',
        confirmNewPassword: 'Conferma nuova password',
        recoveryCodesFilename: 'codici-recupero.txt',
        recoveryCodesDescription:
          'Usa questi codici se perdi l’accesso al tuo autenticatore',
        important: 'Importante:',
        recoveryCodesImportant:
          'Ogni codice può essere usato una sola volta. Conservali in un luogo sicuro.',
      },
    },
    walletIdentity: {
      title: 'Wallet e identità',
      subtitle:
        'Gestisci la tua identità decentralizzata e le impostazioni del wallet',
      walletNotCreatedYet: 'Wallet non ancora creato',
      noWalletToCopy: 'Nessun wallet da copiare',
      walletAddressCopied: 'Indirizzo wallet copiato',
      noDidToCopy: 'Nessun DID da copiare',
      didCopied: 'DID copiato',
      walletExportInitiated: 'Esportazione wallet avviata',
      noWalletToView: 'Nessun wallet da visualizzare',
      yourWalletSecured: 'Il tuo wallet è protetto',
      authenticatedVia: 'Autenticato tramite {{method}}',
      encryptedWithMPC: 'Crittografato con MPC',
      walletPendingCreation: 'Creazione wallet in attesa',
      walletWillBeCreated:
        'Il wallet verrà creato automaticamente alla prima azione on-chain',
      decentralizedIdentity: 'Identità decentralizzata (DID)',
      copyDID: 'Copia DID',
      petsoSecureAccount: 'Account sicuro Petso',
      blockchainWalletAbstracted: 'Wallet blockchain astratto e protetto',
      hide: 'Nascondi',
      viewDetails: 'Visualizza dettagli',
      activeAndSecured: 'Attivo e protetto',
      advancedWalletInfo: 'Informazioni avanzate wallet',
      solanaWalletAddress: 'Indirizzo wallet Solana',
      copyAddress: 'Copia indirizzo',
      viewOnSolscan: 'Visualizza su Solscan',
      securityWarning: 'Avviso di sicurezza',
      neverShareSeedPhrase: 'Non condividere mai la seed phrase con nessuno.',
      hideSeedPhrase: 'Nascondi seed phrase',
      revealSeedPhrase: 'Mostra seed phrase',
      exportWallet: 'Esporta wallet',
      exportConfirmMessage:
        'Conferma l’esportazione. Conserva il file in modo sicuro.',
      confirmExport: 'Conferma esportazione',
      accountRecovery: 'Recupero account',
      accountRecoveredThrough:
        'Puoi recuperare l’account attraverso uno dei metodi seguenti:',
      reVerificationVia: 'Ri-verifica tramite {{method}}',
      passkeyReEnrollment: 'Nuova registrazione passkey',
      seedPhraseIfExported: 'Seed phrase (se esportata in precedenza)',
    },
    subscription: {
      manageBilling: 'Gestisci abbonamento e fatturazione',
      currentPlan: 'Piano attuale',
      yourActiveSubscriptionDetails: 'Dettagli del tuo abbonamento attivo',
      freeAccess: 'Accesso gratuito',
      active: 'Attivo',
      month: 'mese',
      year: 'anno',
      save20: 'Risparmia 20%',
      nextBilling: 'Prossima fatturazione: {{date}}',
      free: 'Gratis',
      includedFree: 'Incluso gratis',
      regularPrice: 'Prezzo standard: {{price}}/{{period}}',
      mo: 'mese',
      yr: 'anno',
      launchPeriodSpecial: 'Offerta periodo di lancio:',
      launchPeriodSpecialDesc: 'attualmente il piano {{plan}} è gratuito.',
      allSubscriptionPlans: 'Tutti i piani di abbonamento',
      choosePlanFitsNeeds: 'Scegli il piano più adatto alle tue esigenze',
      paidPlansSoonEnjoyFree:
        'I piani a pagamento arrivano presto. Goditi l’accesso gratuito ora.',
      monthly: 'Mensile',
      annual: 'Annuale',
      current: 'Attuale',
      popular: 'Popolare',
      forever: 'Per sempre',
      freeFor3Months: 'Gratis per 3 mesi',
      savePerYear: 'Risparmi {{amount}} all’anno',
      thenPerMonth: 'Poi {{price}}/mese',
      comingSoon: 'Prossimamente',
      freeForNow: 'Gratis per ora',
      downgrade: 'Passa al piano gratuito',
      upgrade: 'Aggiorna',
      allPlansFreeBanner:
        'Durante il lancio, tutti i piani premium sono disponibili gratuitamente.',
      invoiceDownloaded: 'Fattura scaricata',
      subscriptionCancelled: 'Abbonamento annullato',
      paidPlansSoonToast: 'I piani a pagamento non sono ancora disponibili.',
      upgradingToPlan: 'Aggiornamento al piano {{plan}}',
      billingSettings: 'Impostazioni di fatturazione',
      manageBillingPreferences:
        'Gestisci preferenze di fatturazione e rinnovo automatico',
      autoRenewal: 'Rinnovo automatico',
      autoRenewalDescription:
        'Rinnova automaticamente l’abbonamento alla fine di ogni periodo di fatturazione',
      billingCycle: 'Ciclo di fatturazione',
      annualSave20: 'Annuale (Risparmia 20%)',
      switchBillingCycle:
        'Passa alla fatturazione {{cycle}} al prossimo rinnovo',
      annualLower: 'annuale',
      monthlyLower: 'mensile',
      paymentMethod: 'Metodo di pagamento',
      managePaymentInformation: 'Gestisci le tue informazioni di pagamento',
      updateCard: 'Aggiorna carta',
      expiresDate: 'Scade {{date}}',
      verified: 'Verificato',
      billingHistory: 'Cronologia fatture',
      viewDownloadPastInvoices: 'Visualizza e scarica le fatture precedenti',
      cancelSubscription: 'Annulla abbonamento',
      downgradeToFreePlan: 'Passa al piano gratuito',
      beforeYouCancel: 'Prima di annullare:',
      cancelWarningDescription:
        'Perderai l’accesso alle funzioni premium al termine del periodo di fatturazione. I tuoi dati saranno conservati, ma alcune funzioni potrebbero essere limitate nel piano gratuito.',
      cancelSubscriptionQuestion: 'Annullare l’abbonamento?',
      cancelUntilDateMessage:
        'Manterrai l’accesso alle funzioni {{plan}} fino al {{date}}, poi passerai automaticamente al piano gratuito.',
      youWillLoseAccessTo: 'Perderai l’accesso a:',
      keepSubscription: 'Mantieni abbonamento',
      yesCancel: 'Sì, annulla',
      planNames: {
        free: 'Gratis',
        freemium: 'Freemium',
        paid: 'A pagamento',
        starterFreemium: 'Starter Freemium',
        starter: 'Starter',
        professional: 'Professionale',
        enterprise: 'Enterprise',
      },
      features: {
        upTo2Pets: 'Fino a 2 animali',
        basicHealthRecords: 'Cartelle sanitarie di base',
        appointmentBooking: 'Prenotazione appuntamenti',
        communitySupport: 'Supporto community',
        basicSocialFeatures: 'Funzionalità social di base',
        unlimitedPets: 'Animali illimitati',
        advancedHealthTracking: 'Monitoraggio salute avanzato',
        prioritySupport: 'Supporto prioritario',
        aiHealthInsights: 'Insight salute con IA',
        premiumSocialFeatures: 'Funzionalità social premium',
        familySharing: 'Condivisione famiglia',
      },
      lossItems: {
        premiumFeatures: 'Funzioni premium',
        prioritySupport: 'Supporto prioritario',
        advancedAnalytics: 'Analisi avanzate',
        increasedLimits: 'Limiti aumentati',
      },
    },
    privacy: {
      globalSearchPrivacy: 'Privacy ricerca globale',
      recommended: 'CONSIGLIATO',
      preview: 'Anteprima',
      privacyDescription:
        'Controlla cosa possono vedere le persone quando cercano il tuo animale tramite DID o Microchip ID. Queste impostazioni aiutano a ritrovare gli animali smarriti proteggendo la tua privacy.',
      ownerBasicInfoVisibility: 'Visibilità informazioni base del proprietario',
      private: 'Privato',
      partialPrivacy: 'Privacy parziale',
      publicContact: 'Contatto pubblico',
      privateOwnerDesc:
        'Nome proprietario non visibile. Il ricercatore vede: "Le informazioni di contatto del proprietario sono private. Contatta hello@petso.io per assistenza."',
      partialOwnerDesc:
        'Visibile solo il nome. Abilita messaggistica sicura tramite Petso per un ricongiungimento sicuro.',
      publicOwnerDesc:
        'Nome completo visibile. Ideale per recupero rapido. Include nota: "Condiviso dal proprietario per finalità di recupero dell’animale."',
      addressVisibility: 'Visibilità indirizzo',
      privateAddressDesc: 'Nessuna informazione di indirizzo visibile.',
      partialAddressDesc:
        'Solo città + paese (es. "Milano, Italia"). Nessun indirizzo stradale.',
      publicAddressDesc:
        'Indirizzo completo visibile, inclusi via, città e CAP.',
      contactInfoVisibility: 'Visibilità informazioni di contatto',
      privateContactDesc:
        'Email e telefono non visibili. Solo contatto sicuro in-app.',
      partialContactDesc:
        'Messaggistica sicura tramite Petso soltanto. Nessuna email/telefono diretti.',
      publicContactDesc: 'Email e telefono visibili per contatto diretto.',
      familyLitterTitle: 'Privacy famiglia e cucciolata',
      familyLitterDesc:
        'Controlla come relazioni genitore-figlio e dati cucciolata appaiono nella Ricerca Globale Pet.',
      familyPrivateDesc: 'Relazioni genitore-figlio nascoste dalla ricerca.',
      familyPartialDesc: 'Relazioni visibili solo agli utenti verificati.',
      familyPublicDesc: 'Relazioni genitore-figlio visibili nella ricerca.',
      partialLabel: 'Parziale',
      publicLabel: 'Pubblico',
      showLitterSize: 'Mostra dimensione cucciolata',
      showLitterSizeDesc:
        'Consenti ai risultati di ricerca di mostrare il numero di cuccioli in una cucciolata.',
      howItWorks:
        'Come funziona: quando qualcuno cerca il tuo animale usando DID o Microchip ID, queste impostazioni privacy determinano quali informazioni del proprietario può vedere. Puoi modificarle in qualsiasi momento.',
    },
    rewards: {
      title: 'Ricompense',
      subtitle: 'Guadagna punti e riscatta ricompense',
      points: 'Punti',
      totalPoints: 'Punti totali',
      earnedToday: 'Guadagnati oggi',
      redeem: 'Riscatta',
      history: 'Cronologia',
      available: 'Ricompense disponibili',
      howToEarn: 'Come guadagnare punti',
      petsoRewards: 'Ricompense Petso',
      noRedeemableRewards:
        'Nessuna ricompensa riscattabile al momento. Le ricompense sono gestite nel pannello Admin e appariranno qui quando disponibili.',
      earnPointsForServices:
        'Guadagna punti offrendo servizi {{role}} eccellenti',
      earnPointsEngagement:
        'Guadagna punti con il coinvolgimento e riscattali per ricompense di valore',
      yourBalance: 'Il tuo saldo',
      earnMoreReferrals: 'Guadagna di piu con i referral',
      inviteFriendsOwner:
        'Invita amici e guadagna 200 PETS quando prenotano il loro primo appuntamento!',
      inviteFriendsPro:
        'Invita proprietari di animali e guadagna 250 PETS quando prenotano con te!',
      startReferring: 'Inizia a invitare',
      needMore: 'Te ne servono ancora {{count}}',
      exclusiveRewards: 'Ricompense esclusive per {{role}}',
      redeemYourPoints: 'Riscatta i tuoi punti',
      roleName: {
        veterinarian: 'Veterinario',
        groomer: 'Toelettatore',
        walker: 'Dog walker',
        trainer: 'Addestratore',
        boarding: 'Pensione / Hotel per animali',
        daycare: 'Asilo diurno',
        other: 'Professionista',
      },
      manageYourRewards: 'Gestisci le tue ricompense',
      claimSendTransferDesc:
        'Richiedi, invia o trasferisci al wallet • Commissioni gas sponsorizzate',
      petsAvailable: 'PETS disponibili',
      claim: 'Richiedi',
      getYourRewards: 'Ottieni le tue ricompense',
      petsReady: 'PETS pronti',
      send: 'Invia',
      toAnotherUser: 'A un altro utente',
      sendViaUsername: 'Invia tramite username o DID',
      transferToWallet: 'Trasferisci al wallet',
      cashOutSelfCustody: 'Preleva o auto-custodia',
      solanaNetwork: 'Rete Solana',
      noRewardsToSend: 'Nessuna ricompensa da inviare',
      noRewardsToTransfer: 'Nessuna ricompensa da trasferire',
      currentTier: 'Livello attuale',
      progressTo: 'Progresso verso {{tier}}',
      pointsToGo: 'punti mancanti',
      tierBronze: '✓ Bonus di benvenuto | ✓ Ricompense standard',
      tierSilver: '✓ 1.5x punti | ✓ Supporto prioritario',
      tierGold: '✓ 2x punti | ✓ Offerte esclusive',
      tierPlatinum: '✓ 3x punti | ✓ Vantaggi VIP',
      earnPoints: 'Guadagna punti',
      redeemRewards: 'Riscatta ricompense',
      tabCommunity: 'Community',
      tabClaims: 'Claim',
      waysToEarn: 'Modi per guadagnare punti',
      pointsOpportunities: 'Opportunita punti per {{role}}',
      noEarningActions: 'Nessuna azione disponibile al momento per guadagnare.',
      start: 'Inizia',
      shareReferralCode: 'Condividi il tuo codice referral',
      createWallet: 'Crea wallet',
      creating: 'Creazione in corso...',
      walletCreated: 'Wallet creato con successo!',
      walletCreatedDesc: 'Il tuo nuovo wallet: {{address}}',
      referralCodeCopied: 'Codice referral copiato negli appunti!',
      shareLinkCopied: 'Link referral copiato negli appunti!',
      shareTextCopied: 'Testo di condivisione copiato negli appunti!',
      totalReferrals: 'Referral totali',
      pending: 'In attesa',
      completed: 'Completati',
      referrals: 'Referral',
      referralsTab: {
        referAndEarn: 'Invita e guadagna',
        yourReferralCode: 'Il tuo codice referral',
        copy: 'Copia',
        share: 'Condividi',
        yourShareableLink: 'Il tuo link condivisibile',
        shareableLinkForOwners:
          'Petso Webapp — link condivisibile per proprietari di animali',
        shareableLinkForProfessionals:
          'Petso Pro — link condivisibile per professionisti pet',
        copyLink: 'Copia link',
        copied: 'Copiato!',
        howReferralsWork: 'Come funzionano i referral',
        inviteOwner: 'Invita un proprietario di animali',
        earn200Owner: 'Guadagna 200 PETS quando prenota il primo appuntamento.',
        inviteProfessional: 'Invita un professionista',
        earn300Pro: 'Guadagna 300 PETS quando completa la prima prenotazione.',
        invitePetOwner: 'Invita un proprietario di animali',
        earn250Owner:
          'Guadagna 250 PETS quando prenota il tuo servizio {{role}}.',
        inviteAnotherProfessional: 'Invita un altro professionista',
        earn400Pro: 'Guadagna 400 PETS quando completa la prima prenotazione.',
      },
      minAgo: '{{n}} min fa',
      hourAgo: '{{n}} ora fa',
      dayAgo: '{{n}} giorno fa',
      devnet: 'Devnet',
      joinPetso: 'Unisciti a Petso',
      successfullyClaimed: '{{amount}} PETS richiesti con successo!',
      failedToClaim: 'Richiesta ricompense non riuscita. Riprova.',
      invalidAmount: 'Importo non valido',
      enterValidAmount: 'Inserisci un importo valido',
      insufficientBalance: 'Saldo insufficiente. Hai {{balance}} PETS',
      amountMustBeWhole: 'L importo deve essere un numero intero',
      enterWalletAddress: 'Inserisci un indirizzo wallet',
      invalidSolanaAddress: 'Indirizzo wallet Solana non valido',
      successfullyTransferred: '{{amount}} PETS trasferiti con successo!',
      transferFailed: 'Trasferimento non riuscito. Riprova.',
      custodialComingSoon:
        'I trasferimenti verso wallet custodial arriveranno presto!',
      rewardRedeemed: '{{title}} riscattato!',
      noLongerAvailable: 'Non piu disponibile',
      left: '{{count}} rimasti',
      useAtBooking: 'Usa in fase di prenotazione',
      codeCopied: 'Codice copiato!',
      redemptionFailed: 'Riscatto non riuscito',
      actions: {
        createPetProfile: 'Crea profilo animale',
        createPetDid: 'Crea DID animale',
        completePetProfile: 'Completa profilo animale',
        bookAppointment: 'Prenota appuntamento',
        leaveReview: 'Lascia una recensione',
        postOnSocial: 'Pubblica sui social',
        dailyAppUse: 'Uso quotidiano dell app',
      },
      redemptionTitle: {
        bookingDiscount: 'Sconto prenotazione',
      },
      redemptionDesc: {
        bookingDiscount: 'Sconto del 10% sul prossimo appuntamento',
      },
      openingTwitter: 'Apertura di Twitter per condividere!',
      openingFacebook: 'Apertura di Facebook per condividere!',
      openingLinkedIn: 'Apertura di LinkedIn per condividere!',
      petsoRewardRedeemed: 'Ricompensa Petso riscattata',
      community: {
        title: 'Ricompense Community',
        subtitle: 'Guadagna PETs aiutandoci a far crescere la community Petso!',
        totalEarned: 'Totale guadagnato',
        completed: 'Completati',
        available: 'Disponibili',
        allTasks: 'Tutti i task',
        socialFollows: 'Follow social',
        engagement: 'Coinvolgimento',
        referrals: 'Referral',
        statusCompleted: 'Completato',
        statusPendingReview: 'In revisione',
        statusInProgress: 'In corso',
        statusNotStarted: 'Non iniziato',
        reward: 'Ricompensa',
        startTask: 'Avvia task',
        submitForVerification: 'Invia per verifica',
        pendingAdminReview: 'In revisione admin',
        completedExcl: 'Completato!',
        youWillEarn: 'Guadagnerai:',
        howToComplete: 'Come completare:',
        shareInviteLink: 'Condividi link invito',
        viewPostToShare: 'Visualizza post da condividere',
        goToPlatform: 'Vai su {{platform}}',
        taskStartedToast:
          'Task avviato! Completa l azione e invia per verifica.',
        joinMeOnPetso:
          'Unisciti a me su Petso - la piattaforma completa per la cura degli animali!',
        noTasksInCategory: 'Nessun task in questa categoria al momento',
        noTasksFromAdmin:
          'Nessun task community al momento. I task vengono creati in Admin → Rewards → Community Rewards.',
        referralLinkCopiedToast: 'Link referral copiato negli appunti!',
        failedToCopyLink: 'Impossibile copiare il link. Riprova.',
        couldNotCopyLink: 'Impossibile copiare automaticamente. Link: {{link}}',
        petsEarnedToast: '+{{count}} PETs guadagnati!',
        taskSubmittedToast: 'Task inviato per verifica!',
        instructionFollow1:
          'Clicca il pulsante qui sotto per aprire {{platform}}',
        instructionFollow2:
          'Segui, metti Mi piace o iscriviti al nostro account',
        instructionFollow3:
          'Fai uno screenshot come prova (opzionale ma utile)',
        instructionFollow4: 'Torna qui e invia per verifica',
        instructionEngage1: 'Clicca il pulsante qui sotto per vedere il post',
        instructionEngage2:
          'Condividi il post sui tuoi social oppure metti Mi piace a piu post',
        instructionEngage3:
          'Fai uno screenshot della tua condivisione/interazione',
        instructionEngage4: 'Torna qui e invia per verifica con prova',
        instructionReferral1:
          'Clicca il pulsante qui sotto per ottenere il tuo link invito',
        instructionReferral2:
          'Condividi il link con amici via WhatsApp, SMS, email, ecc.',
        instructionReferral3:
          'Quando i tuoi amici si registrano, verranno tracciati',
        instructionReferral4:
          'Invia per verifica quando hai invitato il numero richiesto',
        taskKeys: {
          follow_twitter: 'Segui Petso su Twitter/X',
          follow_twitter_desc:
            'Segui @petso_io su X (Twitter) e resta aggiornato sulle ultime novita',
        },
      },
      claims: {
        title: 'Claim',
        airdrops: 'Airdrop',
        icoClaims: 'Claim ICO',
        backToWalletList: 'Torna alla lista wallet (MetaMask / WalletConnect)',
        verifyWalletAirdrop:
          'Verifica il tuo wallet per controllare se sei idoneo agli airdrop $PET.',
        connectWallet: 'Connetti wallet',
        supportedWallets:
          'Supportati: MetaMask, wallet compatibili WalletConnect',
        walletConnected: 'Wallet connesso',
        disconnect: 'Disconnetti',
        checkingEligibility: 'Verifica idoneita in corso...',
        noActiveAirdrops: 'Nessun airdrop attivo trovato per questo wallet.',
        howAirdropsWork: 'Come funzionano gli airdrop?',
        airdropsHowInfo:
          'Come funzionano gli airdrop: eseguiamo campagne e aggiungiamo i wallet idonei nel nostro database. Connetti il wallet che usi con Petso per verificare l idoneita.',
        eligibleLabel: 'Idoneo: {{amount}} $PET',
        claimPet: 'Richiedi $PET',
        claiming: 'Richiesta in corso...',
        allAirdropsClaimed: 'Tutti gli airdrop sono stati riscattati',
        noUnclaimedAirdrops: 'Nessun airdrop non riscosso per questo wallet.',
        refreshEligibility: 'Aggiorna idoneita',
        claimIcoDesc: 'Richiedi i tuoi token $PET dalla prevendita ICO Petso.',
        usePresaleWallet: 'Usa il wallet usato per la prevendita',
        verifyingInvestment: 'Verifica investimento in corso...',
        noIcoParticipation:
          'Nessuna partecipazione ICO trovata per questo wallet.',
        contactSupport: 'Contatta il supporto: hello@petso.io',
        totalPetPurchased: 'Totale $PET acquistati:',
        alreadyClaimed: 'Gia riscattati:',
        vesting: 'Vesting:',
        nextUnlock: 'Prossimo sblocco:',
        availableToClaimNow: 'Disponibile da richiedere ora: {{amount}} PET',
        claimAvailablePet: 'Richiedi $PET disponibili',
        noAmountToClaimNow: 'Nessun importo disponibile da richiedere ora.',
        nextClaim: 'Prossimo claim:',
        claimHistory: 'Cronologia claim',
        refreshVerification: 'Aggiorna verifica',
        airdropClaimedToast: 'Airdrop riscattato!',
        airdropClaimedDesc:
          '{{amount}} PET sono stati accreditati nel tuo wallet Petso.',
        claimFailed: 'Claim non riuscito.',
        icoClaimedToast: 'Token ICO riscattati!',
        icoClaimedDesc:
          '{{amount}} PET sono stati accreditati nel tuo wallet Petso.',
        failedToCheckAirdrop: 'Verifica idoneita airdrop non riuscita.',
        failedToClaimAirdrop: 'Riscatto airdrop non riuscito.',
        failedToVerifyIco: 'Verifica investimento ICO non riuscita.',
        failedToClaimIco: 'Riscatto token ICO non riuscito.',
      },
      walletModals: {
        claimRewards: 'Richiedi ricompense',
        availableToClaim: 'Disponibile da richiedere',
        fromReferralsBookings: 'Da referral, prenotazioni e coinvolgimento',
        networkFee: 'Commissione di rete:',
        sponsored: 'Sponsorizzata',
        claiming: 'Richiesta in corso...',
        confirmAndClaim: 'Conferma e richiedi',
        cancel: 'Annulla',
        sendRewards: 'Invia ricompense',
        availableBalance: 'Saldo disponibile',
        sendTo: 'Invia a',
        selectRecipient: 'Seleziona destinatario...',
        optionUsername: 'Username Petso',
        optionDid: 'DID Pet',
        optionWallet: 'Indirizzo wallet',
        optionQr: 'Scansiona codice QR',
        amount: 'Importo',
        useMax: 'Usa massimo',
        noteOptional: 'Nota (opzionale)',
        addMessage: 'Aggiungi un messaggio...',
        transferToWallet: 'Trasferisci al wallet',
        selectWalletType: 'Seleziona tipo di wallet',
        petsoWallet: 'Wallet Petso',
        externalWallet: 'Wallet esterno',
        walletAddress: 'Indirizzo wallet',
        enterSolanaPlaceholder: 'Inserisci indirizzo wallet Solana...',
        networkSolana: 'Rete: Solana',
        transferAll: 'Trasferisci tutto',
        transferring: 'Trasferimento in corso...',
        transferNow: 'Trasferisci ora',
        rewardsClaimed: 'Ricompense richieste!',
        transferComplete: 'Trasferimento completato',
        fundsTransferred: 'Fondi trasferiti con successo',
        rewardsClaimedDesc: 'Le tue ricompense sono state aggiunte al saldo',
        rewardsSentDesc: 'Ricompense inviate con successo',
        fundsTransferredDesc: 'I tuoi fondi sono stati trasferiti al wallet',
        viewBalance: 'Visualizza saldo',
        sendRewardsBtn: 'Invia ricompense',
        transferToWalletBtn: 'Trasferisci al wallet',
        openingSendFlow: 'Apertura flusso invio...',
        openingTransferFlow: 'Apertura flusso trasferimento...',
      },
      dashboard: {
        pointsHistory: 'Cronologia punti',
        noTransactionHistory:
          'Nessuna cronologia transazioni al momento. Inizia a guadagnare punti per vederla qui!',
        loadMore: 'Carica altro',
        loading: 'Caricamento...',
        aboutPetsoRewards: 'Informazioni sulle Ricompense Petso',
        aboutPetsoRewardsPro:
          'Informazioni sulle Ricompense Petso per {{role}}',
        aboutBullet1:
          'I punti sono ricompense basate sul coinvolgimento, non strumenti finanziari',
        aboutBullet2:
          'Nessuna dipendenza da blockchain o NFT (tranne riscatti NFT solo per divertimento)',
        aboutBullet2Pro:
          'Nessuna dipendenza da blockchain o NFT (tranne riscatti NFT solo per divertimento per {{role}})',
        aboutBullet3: 'I punti scadono dopo 12 mesi di inattivita',
        aboutBullet4:
          'La policy di uso corretto previene abusi e mantiene l integrita del programma',
        aboutBullet5:
          'Riscatta in qualsiasi momento per valore reale nell ecosistema Petso',
        shareRewardWithFriends: 'Condividi questa ricompensa con gli amici',
        redeemedShareAchievement: 'Riscattata! Condividi il tuo risultato',
        shareRewardTitle: 'Ho riscattato una ricompensa su Petso!',
        copy: 'Copia',
      },
    },
    social: {
      title: 'Social',
      subtitle: 'Connettiti con altri proprietari di animali',
      you: 'Tu',
      backToSocial: 'Torna a Social',
      all: 'Tutti',
      allPets: 'Tutti gli animali',
      allPosts: 'Tutti i post',
      myPets: 'I miei animali',
      posts: 'Post',
      tagged: 'Taggati',
      saved: 'Salvati',
      socialActivity: 'Attività social',
      followers: 'Follower',
      following: 'Seguiti',
      failedToLoadList: 'Impossibile caricare la lista',
      noFollowersYet: 'Nessun follower ancora',
      noFollowingYet: 'Non segui ancora nessuno',
      loadMore: 'Carica altro',
      userAccount: 'Utente',
      petProfile: 'Animale',
      createPost: 'Crea post',
      share: 'Condividi',
      like: 'Mi piace',
      liked: 'Piaciuto',
      reply: 'Rispondi',
      replyingTo: 'In risposta a {{name}}',
      replyToCommentPlaceholder: 'Rispondi a {{name}}...',
      comment: 'Commento',
      comments: 'Commenti',
      noCommentsYet: 'Nessun commento ancora',
      post: 'Pubblica',
      public: 'Pubblico',
      private: 'Privato',
      friends: 'Amici',
      friendsPlus: 'Amici+',
      profilePrivacy: 'Privacy profilo',
      privacyProtectedLabel: 'Privacy protetta',
      privacyProtectedMessage:
        'Questo profilo è privato. Solo i follower approvati possono vedere i post.',
      privacyMessagePublic:
        'Questo profilo è pubblico. Tutti possono vedere i tuoi post.',
      privacyMessagePrivate:
        'Questo profilo è privato. Solo i follower approvati possono vedere i post.',
      privacyMessageFriends:
        'Questo profilo è solo per amici. Solo gli amici approvati possono vedere i post.',
      privacyMessageFriendsPlus:
        'Questo profilo è per amici e i loro contatti. Amici+ possono vedere i post.',
      privacyMessageFollowers:
        'Questo profilo è per follower. Solo i follower possono vedere i post.',
      shareFirstPost: 'Condividi il tuo primo post dalla scheda Crea!',
      socialConnections: 'Connessioni social',
      connectDescription:
        'Collega i tuoi account per pubblicare da Petso. Solo le piattaforme collegate appariranno quando crei un post.',
      noProfessionalsYet: 'Nessun professionista su Social ancora.',
      yourPet: 'Il tuo animale',
      unknown: 'Sconosciuto',
      mixed: 'Misto',
      noBioYet: 'Nessuna bio ancora',
      cannotFollow: 'Impossibile seguire questo profilo',
      failedFollow: 'Impossibile seguire. Riprova.',
      failedUnfollow: 'Impossibile smettere di seguire. Riprova.',
      disconnectSuccess: 'Disconnesso.',
      disconnected: '{{platform}} disconnesso.',
      redirectToConnect: 'Reindirizzamento per collegare {{platform}}…',
      connected: '{{platform}} collegato.',
      connectedDemo:
        '{{platform}} collegato (demo). I toggle per la pubblicazione incrociata appariranno quando crei un post.',
      connectionFailed: 'Connessione fallita. Riprova.',
      connectDemoFromCreate:
        '{{platform}} collegato (demo). Puoi pubblicare su più piattaforme da Crea post.',
      close: 'Chiudi',
      postedFromPetParks: 'Pubblicato da Pet Parks',
      tabFollowing: 'Seguiti',
      tabNearby: 'Vicini',
      tabProfessionals: 'Professionisti',
      tabChallenges: 'Challenge',
      tabTrending: 'Di tendenza',
      addComment: 'Aggiungi un commento...',
      writeCaption: 'Scrivi una didascalia...',
      postUpdated: 'Post aggiornato',
      postDeleted: 'Post eliminato',
      linkCopied: 'Link copiato!',
      copyingLink: 'Copia link...',
      savingPost: 'Salvataggio post...',
      demoAction: 'Azione demo',
      sharePost: 'Condividi post',
      changePhoto: 'Cambia foto',
      textPost: 'Post di testo',
      taggedDescription:
        'Post in cui i tuoi animali sono stati taggati (da te o da altri)',
      noTaggedYet:
        'Nessun post in cui i tuoi animali sono stati taggati',
      taggedDescriptionPet:
        'Post in cui {{name}} è stato taggato (da te o da altri)',
      noTaggedYetPet:
        'Nessun post in cui {{name}} è stato taggato',
      postsDescription: 'I tuoi post (foto, video, testo)',
      trendingPets: 'Animali di tendenza',
      explorePosts: 'Esplora post',
      noTrendingPetsYet:
        'Nessun animale di tendenza per ora. Sii il primo a condividere!',
      noPostsToExploreYet:
        'Nessun post da esplorare per ora. Crea un post o torna più tardi.',
      noPostsYet: 'Nessun post ancora',
      beTheFirstToShare:
        'Sii il primo a condividere! Crea un post o esplora per trovarne altri.',
      petFilterLabel: 'Animale:',
      searchPlaceholder: 'Cerca animali, professionisti o hashtag...',
      searchHint: 'Cerca animali, hashtag e post',
      startChatHint: 'Avvia una chat da un profilo o da Pet Parks',
      with: 'Con',
      justNow: 'Adesso',
      timeAgoM: '{{n}}m fa',
      timeAgoH: '{{n}}h fa',
      timeAgoD: '{{n}}g fa',
      loadingFeed: 'Caricamento feed…',
      retry: 'Riprova',
      follow: 'Segui',
      alsoShareToYourStory: 'Condividi anche nella tua storia',
      captionUpdated:
        'Didascalia e tag aggiornati. Le modifiche alle immagini richiedono un nuovo caricamento.',
      failedFollowGeneric: 'Impossibile seguire. Riprova.',
      postShared: 'Post condiviso!',
      failedSharePost: 'Impossibile condividere il post. Riprova.',
      openingInFeed: 'Apertura nel feed',
      messages: {
        title: 'Messaggi',
        searchPlaceholder: 'Cerca messaggi...',
        noConversations: 'Nessuna conversazione ancora',
        selectConversation:
          'Seleziona una conversazione per iniziare a chattare',
        walkingBuddy: 'Compagno di passeggiata',
        newChatWith: 'Nuova chat con {{name}}',
        backToConversations: 'Torna alle conversazioni',
        sendMessageStart: 'Invia un messaggio per iniziare la conversazione',
        viewBookings: 'Visualizza prenotazioni',
        noMessagesYet: 'Nessun messaggio ancora con {{name}}. Saluta!',
        aboutPet: 'Info su {{name}}',
        typeMessage: 'Scrivi un messaggio...',
        sendAppointmentLink: 'Invia link appuntamento',
        searchProfessionals: 'Cerca professionisti',
      },
      notificationsPanel: {
        title: 'Notifiche',
        allCaughtUp: 'Tutto aggiornato!',
        allCaughtUpDesc:
          'Sei aggiornato. Nuovi mi piace, commenti e follow appariranno qui.',
        markAllRead: 'Segna tutto come letto',
        loadError: 'Impossibile caricare le notifiche',
        retry: 'Riprova',
      },
      createPostModal: {
        title: 'Crea post',
        whatToShare: 'Cosa vuoi condividere',
        photo: 'Foto',
        video: 'Video',
        text: 'Testo',
        shareMoment: 'Condividi un momento',
        shareClip: 'Condividi un clip',
        shareUpdate: 'Condividi un aggiornamento',
        uploadFile: 'Carica file',
        dropPhotos: 'Trascina foto qui o sfoglia',
        dropVideo: 'Trascina video qui o sfoglia',
        browseFiles: 'Sfoglia file',
        nextAddDetails: 'Avanti: aggiungi dettagli',
        addPhotoFirst: 'Aggiungi prima una foto o un video',
        general: 'Generale',
        noPetTagged: 'Nessun animale taggato',
        writeCaption: 'Scrivi una didascalia...',
        whereTaken: 'Dove è stato scattato? (opzionale)',
        visibleEveryone: 'Tutti',
        visibleFollowers: 'Solo follower',
        visibleFriends: 'Solo amici',
        visibleFriendsPlus: 'Amici+',
        visiblePrivate: 'Solo io',
        postButton: 'Pubblica',
        placeholderMind: 'A cosa stai pensando?',
        writePostAria: 'Scrivi il tuo post',
        previewAlt: 'Anteprima post',
        searchProfessionals: 'Cerca il tuo veterinario, toelettatore o addestratore...',
        tagProfessionalsLabel: 'Tagga professionisti (opzionale)',
        tagProfessionalsHint:
          'Tagga il tuo veterinario, toelettatore o addestratore per dargli merito!',
      },
      addStoryModal: {
        title: 'Aggiungi storia',
        choosePhotoOrVideo: 'Scegli foto o video',
        fileTypes: 'Foto o video (opzionale)',
        choosePhotoOrVideoToast:
          'Scegli una foto o un video (es. JPG, PNG, MP4).',
        storyAddedToast: 'Storia aggiunta.',
        failedToAddStoryToast: 'Impossibile aggiungere la storia.',
        cropAndEdit: 'Ritaglia e modifica',
        changePhotoOrVideo: 'Cambia foto o video',
        shareToStory: 'Condividi nella storia',
      },
      toast: {
        cannotFollow: 'Impossibile seguire questo profilo',
        followFailed: 'Impossibile seguire. Riprova.',
        unfollowFailed: 'Impossibile smettere di seguire. Riprova.',
        addPhotoBeforePost: 'Aggiungi una foto o un video prima di pubblicare',
        uploadFailed: 'Caricamento fallito',
        uploadFailedStart: 'Caricamento fallito. Riprova.',
      },
      addStory: 'Aggiungi storia',
      yourStory: 'La tua storia',
      backToFeed: 'Torna al feed',
      pets: 'Animali',
      hashtags: 'Hashtag',
      postOptions: 'Opzioni post',
      edit: 'Modifica',
      delete: 'Elimina',
      edited: 'Modificato',
      unlike: 'Non mi piace più',
      savedPosts: 'I tuoi post salvati',
      editPost: 'Modifica post',
      photo: 'Foto',
      caption: 'Didascalia',
      tagPets: 'Tagga animali',
      saveChanges: 'Salva modifiche',
      storyRemoved: 'Storia rimossa.',
      deleteStory: 'Elimina la tua storia',
      previousStory: 'Storia precedente',
      nextStory: 'Storia successiva',
      replyToStory: 'Rispondi alla storia...',
      preview: 'Anteprima',
      cropAndEdit: 'Ritaglia e modifica',
      previewN: 'Anteprima {{n}}',
      filesSelectedSingle: 'file selezionato',
      filesSelectedPlural: 'file selezionati',
      yourPost: 'Il tuo post',
      photoN: 'Foto {{n}}',
      thisIsWhatYouAreSharing: 'Questo è ciò che stai condividendo',
      loadingPets: 'Caricamento animali...',
      noPetsMatchSearch: 'Nessun animale corrisponde alla ricerca.',
      addOrFollowPetsToTag:
        'Aggiungi un animale al tuo profilo o segui altri utenti per taggare i loro animali.',
      searchByPetName: "Cerca per nome dell'animale...",
      addLocationOptional: 'Aggiungi posizione (opzionale)',
      whoCanSeeThis: 'Chi può vedere questo post?',
      anyone: 'Chiunque',
      onlyFollowers: 'Solo follower',
      unfollowedName: 'Hai smesso di seguire {{name}}',
      followingName: 'Ora segui {{name}}!',
      challengeSelected: 'Challenge {{tag}} selezionata',
      searchResultsFor: 'Risultati di ricerca per "{{query}}"',
      postsCountSingle: '{{count}} post',
      postsCountPlural: '{{count}} post',
      noSearchResultsFor:
        'Nessun animale, hashtag o post trovato per "{{query}}".',
      noChallengesYet:
        'Nessuna challenge ancora. Creane una per coinvolgere la community!',
      joinChallenge: 'Partecipa alla challenge',
      howChallengesWorkTitle: 'Come funzionano le challenge',
      howChallengesWorkItem1:
        'Pubblica contenuti con l’hashtag della challenge',
      howChallengesWorkItem2:
        'Nessun premio legato a record o identità dell’animale',
      howChallengesWorkItem3:
        'Solo divertimento e coinvolgimento della community',
      howChallengesWorkItem4: 'I post in evidenza vengono messi in risalto',
      petParks: 'Pet Parks',
      likesComments: '{{likes}} mi piace • {{comments}} commenti',
      failedAddComment: 'Impossibile aggiungere il commento. Riprova.',
      ownedBy: 'Di proprietà di',
      adjustCropAreaFirst: 'Regola prima l’area di ritaglio',
      videoEditingComingSoon:
        'La modifica video arriverà presto. Potrai tagliare i video in un prossimo aggiornamento.',
      useAsIs: 'Usa così com’è',
      crop: 'Ritaglia',
      filter: 'Filtro',
      saving: 'Salvataggio...',
      aspectRatio: 'Proporzioni',
      zoom: 'Zoom',
      zoomHint: 'Pizzica o scorri per zoomare, trascina per riposizionare',
      postsByYouForPet: 'Post pubblicati da te per {{name}}',
      alsoSharedToPlatforms:
        'Condiviso anche su {{names}}. Incluso link CTA a Petso.',
      featuredProfessionals: 'Professionisti in evidenza',
      communityChallenges: '# Challenge della community',
      searchMessages: 'Cerca messaggi...',
      noConversationsYet: 'Nessuna conversazione ancora',
      selectConversation: 'Seleziona una conversazione per iniziare a chattare',
      backToConversations: 'Torna alle conversazioni',
      notification: {
        likeMessage: '{{name}} ha messo mi piace al tuo post',
        commentMessage: '{{name}} ha commentato il tuo post',
        followMessage: '{{name}} ha iniziato a seguirti',
        followAcceptedMessage:
          '{{name}} ha accettato la tua richiesta di follow',
        reactedMessage: '{{name}} ha reagito',
      },
      profile: {
        follow: 'Segui',
        unfollow: 'Smetti di seguire',
        unfollowConfirm: 'Smettere di seguire {{name}}?',
        message: 'Messaggio',
        noBioYet: 'Nessuna bio ancora',
        postDetailView: 'Dettaglio post',
        myProfile: 'Il mio profilo',
        postsFilterLabel: 'Post: {{pet}}',
        selectPet: 'Seleziona animale',
        privacySet: 'Privacy del profilo impostata su {{value}}.',
        privacySetForAllPets:
          'Privacy del profilo impostata su {{value}} per tutti gli animali.',
      },
    },
    previewDialog: {
      emailExample: 'email@esempio.com',
      phoneExample: '+39 312 345 6789',
    },
  },
  petNameEdit: {
    editName:
      'Modifica nome (solo proprietario). DID e Microchip rimangono fissi.',
    required: 'Il nome è obbligatorio.',
    saved: 'Nome aggiornato. DID e Microchip rimangono invariati.',
    error: 'Aggiornamento del nome non riuscito. Riprova.',
    didMicrochipFixed:
      "DID e Microchip sono fissi e non possono essere modificati. Solo il proprietario può modificare il nome dell'animale.",
  },
};

const commonEnMerged = deepMerge(
  commonEn as unknown as JsonObject,
  commonEnOverrides
);

const commonPlOverrides: JsonObject = {
  social: {
    backToSocial: 'Powrót do Social',
    searchPlaceholder: 'Szukaj zwierząt, profesjonalistów lub hashtagów...',
    searchHint: 'Szukaj zwierząt, hashtagów i postów',
    startChatHint: 'Rozpocznij czat z profilu lub z Pet Parks',
    tabFollowing: 'Obserwowani',
    tabNearby: 'W pobliżu',
    tabProfessionals: 'Specjaliści',
    tabChallenges: 'Wyzwania',
    tabTrending: 'Trendujące',
    allPosts: 'Wszystkie posty',
    myPets: 'Moje zwierzęta',
    petFilterLabel: 'Zwierzę:',
    all: 'Wszystkie',
    followers: 'Obserwujący',
    following: 'Obserwowani',
    public: 'Publiczne',
    private: 'Prywatne',
    friends: 'Znajomi',
    friendsPlus: 'Znajomi+',
    profilePrivacy: 'Prywatność profilu',
    privacyProtectedLabel: 'Prywatność chroniona',
    privacyMessagePublic:
      'Ten profil jest publiczny. Wszyscy mogą zobaczyć Twoje posty.',
    privacyMessagePrivate:
      'Ten profil jest prywatny. Tylko zatwierdzeni obserwujący mogą zobaczyć posty.',
    privacyMessageFriends:
      'Ten profil jest tylko dla znajomych. Tylko zatwierdzeni znajomi mogą zobaczyć posty.',
    privacyMessageFriendsPlus:
      'Ten profil jest dla znajomych i ich kontaktów. Znajomi+ mogą zobaczyć posty.',
    privacyMessageFollowers:
      'Ten profil jest dla obserwujących. Tylko obserwujący mogą zobaczyć posty.',
  },
};

const commonEsOverrides: JsonObject = {
  social: {
    backToSocial: 'Volver a Social',
    searchPlaceholder: 'Buscar mascotas, profesionales o hashtags...',
    searchHint: 'Busca mascotas, hashtags y publicaciones',
    startChatHint: 'Inicia un chat desde un perfil o Pet Parks',
    tabFollowing: 'Siguiendo',
    tabNearby: 'Cercanos',
    tabProfessionals: 'Profesionales',
    tabChallenges: 'Desafíos',
    tabTrending: 'Tendencias',
    allPosts: 'Todas las publicaciones',
    myPets: 'Mis mascotas',
    petFilterLabel: 'Mascota:',
    all: 'Todas',
    followers: 'Seguidores',
    following: 'Siguiendo',
    public: 'Público',
    private: 'Privado',
    friends: 'Amigos',
    friendsPlus: 'Amigos+',
    profilePrivacy: 'Privacidad del perfil',
    privacyProtectedLabel: 'Privacidad protegida',
    privacyMessagePublic:
      'Este perfil es público. Todos pueden ver tus publicaciones.',
    privacyMessagePrivate:
      'Este perfil es privado. Solo los seguidores aprobados pueden ver las publicaciones.',
    privacyMessageFriends:
      'Este perfil es para amigos. Solo los amigos aprobados pueden ver las publicaciones.',
    privacyMessageFriendsPlus:
      'Este perfil es para amigos y sus conexiones. Amigos+ pueden ver las publicaciones.',
    privacyMessageFollowers:
      'Este perfil es para seguidores. Solo los seguidores pueden ver las publicaciones.',
  },
};

const commonNlOverrides: JsonObject = {
  social: {
    backToSocial: 'Terug naar Social',
    searchPlaceholder: 'Zoek huisdieren, professionals of hashtags...',
    searchHint: 'Zoek huisdieren, hashtags en posts',
    startChatHint: 'Start een chat vanuit een profiel of Pet Parks',
    tabFollowing: 'Volgend',
    tabNearby: 'In de buurt',
    tabProfessionals: 'Professionals',
    tabChallenges: 'Uitdagingen',
    tabTrending: 'Trending',
    allPosts: 'Alle berichten',
    myPets: 'Mijn huisdieren',
    petFilterLabel: 'Huisdier:',
    all: 'Alle',
    followers: 'Volgers',
    following: 'Volgend',
    public: 'Openbaar',
    private: 'Privé',
    friends: 'Vrienden',
    friendsPlus: 'Vrienden+',
    profilePrivacy: 'Profielprivacy',
    privacyProtectedLabel: 'Privacy beschermd',
    privacyMessagePublic:
      'Dit profiel is openbaar. Iedereen kan je berichten zien.',
    privacyMessagePrivate:
      'Dit profiel is privé. Alleen goedgekeurde volgers kunnen berichten zien.',
    privacyMessageFriends:
      'Dit profiel is alleen voor vrienden. Alleen goedgekeurde vrienden kunnen berichten zien.',
    privacyMessageFriendsPlus:
      'Dit profiel is voor vrienden en hun contacten. Vrienden+ kunnen berichten zien.',
    privacyMessageFollowers:
      'Dit profiel is voor volgers. Alleen volgers kunnen berichten zien.',
  },
};

const commonFrOverrides: JsonObject = {
  social: {
    backToSocial: 'Retour à Social',
    searchPlaceholder:
      'Rechercher des animaux, des professionnels ou des hashtags...',
    searchHint: 'Recherchez des animaux, hashtags et publications',
    startChatHint: 'Démarrez un chat depuis un profil ou Pet Parks',
    tabFollowing: 'Abonnements',
    tabNearby: 'À proximité',
    tabProfessionals: 'Professionnels',
    tabChallenges: 'Défis',
    tabTrending: 'Tendances',
    allPosts: 'Toutes les publications',
    myPets: 'Mes animaux',
    petFilterLabel: 'Animal :',
    all: 'Tous',
    followers: 'Abonnés',
    following: 'Abonnements',
    public: 'Public',
    private: 'Privé',
    friends: 'Amis',
    friendsPlus: 'Amis+',
    profilePrivacy: 'Confidentialité du profil',
    privacyProtectedLabel: 'Profil privé',
    privacyMessagePublic:
      'Ce profil est public. Tout le monde peut voir vos publications.',
    privacyMessagePrivate:
      'Ce profil est privé. Seuls les abonnés approuvés peuvent voir les publications.',
    privacyMessageFriends:
      'Ce profil est réservé aux amis. Seuls les amis approuvés peuvent voir les publications.',
    privacyMessageFriendsPlus:
      'Ce profil est pour les amis et leurs contacts. Les amis+ peuvent voir les publications.',
    privacyMessageFollowers:
      'Ce profil est pour les abonnés. Seuls les abonnés peuvent voir les publications.',
  },
};

const commonDeOverrides: JsonObject = {
  social: {
    backToSocial: 'Zurück zu Social',
    searchPlaceholder: 'Suche nach Haustieren, Profis oder Hashtags...',
    searchHint: 'Suche nach Haustieren, Hashtags und Beiträgen',
    startChatHint: 'Starte einen Chat über ein Profil oder Pet Parks',
    tabFollowing: 'Folgt',
    tabNearby: 'In der Nähe',
    tabProfessionals: 'Profis',
    tabChallenges: 'Challenges',
    tabTrending: 'Trending',
    allPosts: 'Alle Beiträge',
    myPets: 'Meine Haustiere',
    petFilterLabel: 'Haustier:',
    all: 'Alle',
    followers: 'Follower',
    following: 'Folgt',
    public: 'Öffentlich',
    private: 'Privat',
    friends: 'Freunde',
    friendsPlus: 'Freunde+',
    profilePrivacy: 'Profil-Privatsphäre',
    privacyProtectedLabel: 'Privatsphäre geschützt',
    privacyMessagePublic:
      'Dieses Profil ist öffentlich. Alle können deine Beiträge sehen.',
    privacyMessagePrivate:
      'Dieses Profil ist privat. Nur bestätigte Follower können Beiträge sehen.',
    privacyMessageFriends:
      'Dieses Profil ist nur für Freunde. Nur bestätigte Freunde können Beiträge sehen.',
    privacyMessageFriendsPlus:
      'Dieses Profil ist für Freunde und deren Kontakte. Freunde+ können Beiträge sehen.',
    privacyMessageFollowers:
      'Dieses Profil ist für Follower. Nur Follower können Beiträge sehen.',
  },
};

// Backward-compatible guard: some manually added sections can end up nested under
// settings.* by mistake. Promote known top-level namespaces so t('...') resolves.
const normalizedCommonItOverrides = deepMerge(
  {} as JsonObject,
  commonItOverrides
);
const maybeSettings = normalizedCommonItOverrides.settings as
  | JsonObject
  | undefined;
if (
  maybeSettings &&
  typeof maybeSettings === 'object' &&
  'social' in maybeSettings &&
  !('social' in normalizedCommonItOverrides)
) {
  normalizedCommonItOverrides.social = maybeSettings.social;
}
if (
  maybeSettings &&
  typeof maybeSettings === 'object' &&
  'rewards' in maybeSettings &&
  !('rewards' in normalizedCommonItOverrides)
) {
  normalizedCommonItOverrides.rewards = maybeSettings.rewards;
}

const commonIt = deepMerge(commonEnMerged, normalizedCommonItOverrides);
const commonPlMerged = deepMerge(
  commonPl as unknown as JsonObject,
  commonPlOverrides
);
const commonEsMerged = deepMerge(
  commonEs as unknown as JsonObject,
  commonEsOverrides
);
const commonNlMerged = deepMerge(
  commonNl as unknown as JsonObject,
  commonNlOverrides
);
const commonFrMerged = deepMerge(
  commonFr as unknown as JsonObject,
  commonFrOverrides
);
const commonDeMerged = deepMerge(
  commonDe as unknown as JsonObject,
  commonDeOverrides
);

export const resources = {
  [LANGUAGES.EN]: {
    translation: {
      ...translationEn,
      ...(commonEnMerged as typeof commonEn),
    },
  },
  [LANGUAGES.PL]: {
    translation: {
      ...translationPl,
      ...(commonPlMerged as typeof commonPl),
    },
  },
  [LANGUAGES.ES]: {
    translation: {
      ...(commonEsMerged as typeof commonEs),
    },
  },
  [LANGUAGES.NL]: {
    translation: {
      ...translationNl,
      ...(commonNlMerged as typeof commonNl),
    },
  },
  [LANGUAGES.FR]: {
    translation: {
      ...translationFr,
      ...(commonFrMerged as typeof commonFr),
    },
  },
  [LANGUAGES.DE]: {
    translation: {
      ...translationDe,
      ...(commonDeMerged as typeof commonDe),
    },
  },
  [LANGUAGES.IT]: {
    translation: {
      hello: 'Ciao',
      ...commonIt,
    },
  },
};

const SUPPORTED_LANGS = ['en', 'pl', 'es', 'nl', 'fr', 'de', 'it'] as const;
type SupportedLng = (typeof SUPPORTED_LANGS)[number];

function getStoredLanguage(): string {
  try {
    return typeof localStorage !== 'undefined'
      ? localStorage.getItem('language') || 'en'
      : 'en';
  } catch {
    return 'en';
  }
}

function normalizeLng(code: string | undefined): SupportedLng {
  if (!code) return 'en';
  const base = code.split('-')[0].toLowerCase();
  return SUPPORTED_LANGS.includes(base as SupportedLng)
    ? (base as SupportedLng)
    : 'en';
}

i18n.use(initReactI18next).init({
  lng: getStoredLanguage(),
  fallbackLng: 'en',
  resources,
  supportedLngs: ['en', 'pl', 'es', 'nl', 'fr', 'de', 'it'],
  nonExplicitSupportedLngs: true, // e.g. pl-PL uses pl bundle
  load: 'languageOnly', // use only 'pl' not 'pl-PL' when loading resources
  interpolation: {
    escapeValue: false,
  },
});

// Keep document and localStorage in sync whenever language changes (from selector or elsewhere)
i18n.on('languageChanged', (lng: string) => {
  const normalized = normalizeLng(lng);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('language', normalized);
    }
  } catch {
    // ignore
  }
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', normalized);
  }
});

export default i18n;
